import {
  makeEnum, lookupData,
  interpolateInMap,
} from '../Base.js'
import { valOr, getShortMonthName, getHourString, getMonthHourString, } from '../SharedUtils.js'

import { DoorColor, } from '../Components/DoorType.js'
import { ShadesOrientation } from '../Components/InteriorShadingType.js'
import { FloorType } from '../Components/Floor.js'
import { getSeasonOfMonth } from '../Components/Common.js'
import { Units, getLabel, } from '../Common/Units.js'
import { Quantity } from '../Common/Quantity.js'

import { gApp, DebugOn } from '../Globals.js'

import { InfiltrationHours } from './Space.js'
import { CalcPsychrometrics, PsyCalcMethod, adjustForAltitude } from '../Components/Psychrometrics.js'
import * as solar from '../Components/SolarCalculations.js'
import { ShadedAreasCalculator } from './ShadedAreasCalculator.js'
import { CalcContext } from '../Common/CalcContext.js'
import { MonthHourMap } from './MonthHourMap.js'

import {
  MatrixUtils, makeVector,
  positiveModulo,
  toDegs,
  toRads,
} from '../Common/Math.js'
import * as math from 'mathjs'
import { IACCalculator } from '../Components/IACCalculator.js'
import { findMaxLoadForMonth, findMaxLoadInMatrix } from './LoadUtils.js'

import {
  ResultsNode,
  ResultsNodeType,
  LineChartNode,
  MultiLineChartNode,
  StackedBarChartNode,
  SunburstNode,
  PolarPlotNode,
  SummaryTableNode,
} from './ResultsTree.js'

let DoorAlphaMap = {
  [DoorColor.Light]: 0.30,
  [DoorColor.Medium]: 0.60,
  [DoorColor.Dark]: 0.90,
};

export function reverseAboutStart(arr) {
  let copy = math.clone(arr)
  for (let i = 0; i < arr.length; ++i) {
    copy[i] = arr[positiveModulo(-i, arr.length)]
  }
  return copy
}

let RadiantLoadType = makeEnum({
  Solar: 'Solar',
  NonSolar: 'NonSolar',
})

// TODO - move to own file (as well as tests)
// Maybe LoadUtils
export class TimeSeriesUtils {
  static transformVec(ctx, vecToTransform, timeSeriesVec) {
    let numHrs = vecToTransform.length
    let reversedTimeSeriesVec = reverseAboutStart(timeSeriesVec)
    let res = makeVector(numHrs)
    for (let hr = 0; hr < numHrs; ++hr) {
      let adjustmentVec = makeVector(numHrs)
      for (let j = 0; j < numHrs; ++j) {
        adjustmentVec[(hr + j) % numHrs] = reversedTimeSeriesVec[j]
      }
      res[hr] = math.dot(vecToTransform, adjustmentVec)
    }
    return res
  }

  static transformMatrix(ctx, matrixToTransform, timeSeriesVec) {
    ctx.startSection("timeSeriesTransformMatrix")
    let numMonths = matrixToTransform.size()[0]
    let numHrs = matrixToTransform.size()[1]
    ctx.res = math.zeros(numMonths, numHrs)
    for (let i = 0; i < numMonths; i++) {
      let row = MatrixUtils.getRow(matrixToTransform, i)
      let adjustedVec = this.transformVec(ctx, row, timeSeriesVec)
      MatrixUtils.setRow(ctx.res, i, adjustedVec)
    }
    let res = ctx.res
    ctx.logLoadMatrix('res', res)
    ctx.endSection()
    return res
  }
}

export class SpaceCoolingCalculator {
  constructor(space, ctx, opts) {
    this.space = space
    this.ctx = ctx
    this.opts = valOr(opts, {});
  }

  getSummerIndoorTemp(hourIndex) {
    let ctx = this.ctx;
    // Note - we used to use the occupancy schedule for this, but now we just use
    // a fixed value.
    return ctx.t_i_summer;
  }

  getSummerOutdoorTemp(monthIndex, hourIndex) {
    // Retrieve the summer outdoor temp from the design data
    let ctx = this.ctx;
    return ctx.designTemps.getCoolingDryBulbOut(this.ctx, monthIndex, hourIndex);
  }

  getSummerOutdoorWetBulbTemp(monthIndex, hourIndex) {
    // Retrieve the summer outdoor temp from the design data
    let ctx = this.ctx;
    return ctx.designTemps.getCoolingWetBulbOut(this.ctx, monthIndex, hourIndex);
  }

  async calcInfiltrationLatentLoads() {
    let ctx = this.ctx
    ctx.setProgressText("Calculating infiltration latent loads")
    ctx.startSection("Infiltration Latent Loads")
    ctx.I_lat = math.zeros(12, 24)
    ctx.C_l = ctx.call(adjustForAltitude, 4840, ctx.P_loc)
    let breakdown = {}
    for (let month = 0; month < 12; month++) {
      ctx.startLocalSection(`Month ${month}`)
      ctx.Q_inf = this.space.calcInfiltrationFlowRate(ctx, getSeasonOfMonth(month)).Q_inf;
      for (let hour = 0; hour < 24; hour++) {
        //console.log(`Month ${month}, Hour ${hour}`)
        ctx.setProgressText(`Infiltration latent loads - Month ${month}, Hour ${hour}`)
        await ctx.briefWait()
        ctx.startLocalSection(`Hour ${hour}`)
        ctx.t_i = this.getSummerIndoorTemp(hour)
        ctx.W_in = ctx.call(CalcPsychrometrics, ctx.t_i, ctx.altitude,
          PsyCalcMethod.CalcWithRelativeHumidity, {
            RH: ctx.summerIndoorRH
          }
        ).W;
        ctx.t_out_db = this.getSummerOutdoorTemp(month, hour)
        ctx.t_out_wb = this.getSummerOutdoorWetBulbTemp(month, hour)
        ctx.W_out = ctx.call(CalcPsychrometrics, ctx.t_out_db, ctx.altitude,
          PsyCalcMethod.CalcWithWetBulbTemp, {
            t_wb_F: ctx.t_out_wb,
          }
        ).W;
        ctx.I_lat_item = ctx.eval('Q_inf*C_l*(W_out - W_in)', {}, 'I_lat_item')
        ctx.I_lat.set([month, hour], ctx.I_lat_item)
        ctx.endSection()
      }
      ctx.endSection()
    }

    ctx.logLoadMatrix('I_lat', ctx.I_lat)
    
    // Add to breakdown
    breakdown["Infiltration"] = {
      label: "Infiltration Latent Load",
      value: ctx.I_lat,
    }
    
    let res = {
      LatentLoadsMatrix: ctx.I_lat,
      Breakdown: breakdown,
    }

    ctx.endSection()
    return res
  }

  async calcApplianceLatentLoads() {
    let ctx = this.ctx;
    ctx.setProgressText("Calculating appliance latent loads")
    ctx.startSection(`Appliance Latent Loads`)

    let appliancesObj = this.space.internals.appliances;
    let breakdown = {}

    ctx.Appliances_latent = math.zeros(12, 24)
    for (let i = 0; i < appliancesObj.appliances.length; i++) {
      ctx.startLocalSection(`Appliance ${i}`)
      let app = appliancesObj.appliances[i];
      ctx.quantity = app.getQuantity();
      ctx.A_lat_app = app.getLatentLoad(ctx);
      ctx.appSched = app.getSchedule().getData();
      ctx.A_lat_row = new Array(24).fill(0);
      for (let hr = 0; hr < 24; hr++) {
        ctx.startLocalSection(`Hour ${hr}`)
        ctx.Load = ctx.eval('N*A_lat_app*schedFactor', {
          N: ctx.quantity,
          schedFactor: ctx.appSched[hr],
        }, 'Load')
        ctx.A_lat_row[hr] = ctx.Load;
        ctx.endSection()
      }
      ctx.Appliance_latent = MatrixUtils.createMatrixFromRow(ctx.A_lat_row, 12)
      ctx.Appliances_latent = math.add(ctx.Appliances_latent, ctx.Appliance_latent) 
      
      // Add to breakdown
      let key = `Appliance${i+1}`;
      let quantityStr = ctx.quantity > 1 ? ` x${ctx.quantity}` : '';
      breakdown[key] = {
        label: `Appliance ${i + 1}${quantityStr}`,
        value: ctx.Appliance_latent,
      }
      
      ctx.endSection()
    }

    ctx.log('Results:')
    ctx.logLoadMatrix('Appliances_latent', ctx.Appliances_latent)

    let res = {
      LatentLoadsMatrix: ctx.Appliances_latent,
      Breakdown: breakdown,
    }

    ctx.endSection()
    return res;
  }

  async calcPeopleLatentLoads() {
    let ctx = this.ctx;
    ctx.setProgressText("Calculating people latent loads")
    ctx.startSection(`People Latent Loads`)
    let people = this.space.internals.people;
    ctx.sched = people.getSchedule().getData();
    ctx.M = people.getNumOccupants().result;
    ctx.activityLevel = people.activityLevel.value;
    let activityLevelData = people.getActivityLevelData(ctx);
    ctx.L = activityLevelData.Latent_Heat;
    ctx.P_lat_mo0 = ctx.eval(`sched * M * L`, {}, 'P_lat')
    // Same for each month:
    ctx.P_lat = math.zeros(12, 24)
    for (let i = 0; i < 12; i++) {
      MatrixUtils.setRow(ctx.P_lat, i, ctx.P_lat_mo0)
    }
    ctx.logLoadMatrix('P_lat', ctx.P_lat)
    
    let breakdown = {
      "People": {
        label: "People Latent Load",
        value: ctx.P_lat,
      }
    };
    
    let res = {
      LatentLoadsMatrix: ctx.P_lat,
      Breakdown: breakdown,
    }
    ctx.endSection()
    return res
  }

  async calcMiscLatentLoads() {
    let ctx = this.ctx;
    ctx.setProgressText("Calculating misc latent loads")
    ctx.startSection(`Misc Latent Loads`)
    ctx.M_lat_row = new Array(24).fill(0);
    let breakdown = {};

    // Note: there is only one misc load entry for now
    let miscLoads = [this.space.internals.miscLoads];
    ctx.assert(miscLoads.length == 1, "Only one misc load entry is supported for now")
    let misc = miscLoads[0];
    ctx.M_entry = misc.getLatentLoad(ctx);
    ctx.entrySched = misc.getSchedule().getData();
    ctx.M_lat_row = ctx.eval('M_entry * entrySched', {}, 'M_lat_row')
    ctx.M_lat = MatrixUtils.createMatrixFromRow(ctx.M_lat_row, 12)
    
    // Add to breakdown
    breakdown[`Misc`] = {
      label: `Miscellaneous Load`,
      value: ctx.M_lat,
    }

    ctx.logLoadMatrix('M_lat', ctx.M_lat)
    let res = {
      LatentLoadsMatrix: ctx.M_lat,
      Breakdown: breakdown,
    }

    ctx.endSection()
    return res
  }

  /**
   * 
   * Returns the result of calc_solar_radiation, which is {E_t, E_t_b, E_t_d, E_t_r}
   */
  calc_solar_radiation(wall, monthIndex, hrIndex) {
    let ctx = this.ctx;
    ctx.startSection("Calc E_t")
    let locData = ctx.toplevelData.locationData;
    ctx.wallDir = wall.direction.value
    ctx.groundReflectance = ctx.buildingAndEnv.getReflectance(monthIndex, ctx.wallDir)
    ctx.dayOfYear = ctx.eval('getDayOfYear(monthIndex, dayOfMonth)', {
      getDayOfYear: solar.getDayOfYear,
      monthIndex,
      dayOfMonth: ctx.toplevelData.dayOfMonth,
    }, 'dayOfYear')
    ctx.tau_b = locData.tauBValuesByMonth[monthIndex]
    ctx.tau_d = locData.tauDValuesByMonth[monthIndex]
    ctx.E_t_data = ctx.call(solar.calc_solar_radiation,
      locData.timezone,
      hrIndex,
      ctx.dayOfYear,
      locData.latitude,
      locData.longitude, 
      ctx.tau_b,
      ctx.tau_d,
      wall.direction.value,
      wall.getTiltAngleDegs(),
      ctx.groundReflectance,
    );
    let res = ctx.E_t_data
    ctx.endSection()
    return res
  }

  calc_t_e(wall, moIndex, hrIndex, t_out, alpha) {
    //! This is the sol-air temperature. See Ch. 21 of ASHRAE manual (RTS Cooling Calcs)
    //! It is the temp that, when used in heat transfer equation, is equivalent to
    //! to the heat transfer of outdoor air + incident radiation on the surface. Works
    //! By adding an incident radiation factor here (can have a large effect!).
    let ctx = this.ctx;
    ctx.startSection("Calc t_e")
    ctx.E_t = this.calc_solar_radiation(wall, moIndex, hrIndex).E_t
    ctx.t_e = ctx.eval('t_out + alpha*E_t/h_o + epsilon*deltaR/h_o', {
      t_out: t_out,
      alpha: alpha,
      h_o: 3.0,
      epsilon: 1,
      deltaR: wall.isRoof() ? 20 : 0,
    }, 't_e')
    let res = ctx.t_e
    ctx.endSection()
    return res
  }

  applyRTSTransform(matrix, radiantLoadType) {
    let ctx = this.ctx;
    ctx.startSection(`Apply RTS transform - ${radiantLoadType}`)
    ctx.logLoadMatrix('Before transform', matrix)
    let timeSeriesData = ctx.tablesCache.getTable('TimeSeriesData');
    if (radiantLoadType == RadiantLoadType.Solar) {
      ctx.rtsVec = timeSeriesData.getSolarRTSValues(this.space.getRTSTransformArgs())
    } else {
      ctx.rtsVec = timeSeriesData.getNonSolarRTSValues(this.space.getRTSTransformArgs())
    }
    ctx.transformedMatrix = TimeSeriesUtils.transformMatrix(ctx, matrix, ctx.rtsVec)
    ctx.logLoadMatrix('After transform', ctx.transformedMatrix)
    let res = ctx.transformedMatrix
    ctx.endSection()
    return res
  }

  /**
   * 
   * isWall - true if wall, false if roof
   */
  async calcWallLoads(wall) {
    let ctx = this.ctx
    ctx.startSection(`${wall.isRoof() ? 'Roof' : 'Wall'} Loads`)
    ctx.U = 1.0 / wall.getWallType().getRValue()
    ctx.alpha = wall.getWallType().getAbsorptance()
    ctx.A = wall.getStrictlyWallArea()
    ctx.W_loads = math.zeros(12, 24)
    for (let monthIndex = 0; monthIndex < 12; ++monthIndex) {
      ctx.startLocalSection(`Month ${monthIndex}`)
      for (let hrIndex = 0; hrIndex < 24; ++hrIndex) {
        ctx.startLocalSection(`Hour ${hrIndex}`)
        ctx.setProgressText(`Calculating ${wall.isRoof() ? 'roof' : 'wall'} loads - Month ${monthIndex}, Hour ${hrIndex}`)
        await ctx.briefWait()
        ctx.t_in = this.getSummerIndoorTemp(hrIndex)
        ctx.t_out = this.getSummerOutdoorTemp(monthIndex, hrIndex)
        ctx.t_e = this.calc_t_e(wall, monthIndex, hrIndex, ctx.t_out, ctx.alpha)
        ctx.W_mo_hr = ctx.eval('U*A*(t_e - t_in)', {}, 'W_mo_hr')
        ctx.W_loads.set([monthIndex, hrIndex], ctx.W_mo_hr)
        ctx.endSection()
      }
      ctx.endSection()
    }
    let res = ctx.W_loads
    ctx.logLoadMatrix('W_loads', ctx.W_loads)
    ctx.endSection()
    return res
  }

  /**
   * 
   * isWall: true if wall, false if roof
   */
  async calcWallSensibleLoads(isWall) {
    let ctx = this.ctx;
    ctx.startSection(isWall ? "Walls" : "Roofs")

    let timeSeriesData = ctx.tablesCache.getTable('TimeSeriesData');
    let walls = isWall ? this.space.walls : this.space.roofs;
    // Note: the plenum loads are kept separate from the sensible loads
    ctx.Walls_sensible = math.zeros(12, 24)
    ctx.Walls_plenum = math.zeros(12, 24)
    let breakdown = {}
    
    for (let i = 0; i < walls.length; ++i) {
      let wall = walls[i];
      ctx.startLocalSection(`${isWall ? 'Wall' : 'Roof'} ${i}`)
      ctx.F_plenum = wall.getPlenumLoadFraction();
      ctx.Wall_loads = await this.calcWallLoads(wall)

      if (isWall) {
        ctx.wallCTSVec = timeSeriesData.getCTSValuesForWallType(wall.getWallType())
      } else {
        ctx.wallCTSVec = timeSeriesData.getCTSValuesForRoofType(wall.getRoofType())
      }
      ctx.Wall_CTS = TimeSeriesUtils.transformMatrix(ctx, ctx.Wall_loads, ctx.wallCTSVec)

      ctx.F_conv_wall = isWall ? 0.54 : 0.40
      ctx.Wall_conv = ctx.eval('(1 - F_plenum)*Wall_CTS*F_conv_wall', {
      }, 'W_conv')

      ctx.Wall_rad_nonsolar = ctx.eval('(1 - F_plenum)*Wall_CTS*(1 - F_conv_wall)', {
      }, 'W_rad_nonsolar')
      ctx.Wall_rad_nonsolar = this.applyRTSTransform(ctx.Wall_rad_nonsolar, RadiantLoadType.NonSolar)

      ctx.Wall_sensible = MatrixUtils.sumOfMatrices([ctx.Wall_conv, ctx.Wall_rad_nonsolar])
      ctx.Walls_sensible = math.add(ctx.Walls_sensible, ctx.Wall_sensible)

      ctx.Wall_plenum = ctx.eval('F_plenum*Wall_CTS', {}, 'W_plenum')
      ctx.Walls_plenum = math.add(ctx.Walls_plenum, ctx.Wall_plenum)

      // Add to breakdown
      let wallName = isWall ? wall.getWallType().name.value : wall.getRoofType().name.value;
      let uValue = 1.0 / wall.getWallType().getRValue()
      let key = `${isWall ? 'Wall' : 'Roof'}${i+1}`;
      breakdown[key] = {
        label: `${isWall ? 'Wall' : 'Roof'} ${i + 1}: ${wallName}`,
        value: ctx.Wall_sensible,
        metadata: {
          uValue: uValue,
          area: wall.getStrictlyWallArea(),
        }
      }

      ctx.logLoadMatrix('Wall_conv', ctx.Wall_conv)
      ctx.logLoadMatrix('Wall_rad_nonsolar', ctx.Wall_rad_nonsolar)
      ctx.logLoadMatrix('Wall_sensible', ctx.Wall_sensible)
      ctx.logLoadMatrix('Wall_plenum', ctx.Wall_plenum)

      ctx.endSection()
    }

    ctx.logLoadMatrix('Walls_sensible', ctx.Walls_sensible)
    ctx.logLoadMatrix('Walls_plenum', ctx.Walls_plenum)
    let res = {
      SensibleLoadsMatrix: ctx.Walls_sensible,
      PlenumLoadsMatrix: ctx.Walls_plenum,
      Breakdown: breakdown,
    }

    ctx.endSection()
    return res
  }

  async calcWindowNonSolarLoad(wall, window) {
    /**
     * The nonsolar loads are split into convective and radiative loads.
     * Return the two matrices.
     */
    let ctx = this.ctx;
    ctx.startSection("Window Nonsolar Load")

    ctx.Window_loads_conv = math.zeros(12, 24)
    ctx.Window_loads_rad = math.zeros(12, 24)

    let windowType = window.getWindowType()
    ctx.U = windowType.computeUValue().uValue
    ctx.A = windowType.getArea()
    ctx.SHGC = windowType.computeShgc()
    for (let mo = 0; mo < 12; ++mo) {
      ctx.startLocalSection(`Month ${mo}`)
      for (let hr = 0; hr < 24; ++hr) {
        ctx.startLocalSection(`Hour ${hr}`)
        ctx.setProgressText(`Calculating window nonsolar loads - Month ${mo}, Hour ${hr}`)
        await ctx.briefWait()

        ctx.t_in = this.getSummerIndoorTemp(hr)
        ctx.t_out = this.getSummerOutdoorTemp(mo, hr)
        ctx.E_t_data = this.calc_solar_radiation(wall, mo, hr)
        ctx.SHGC_angle = this.calcSHGCForAngle(ctx.SHGC, ctx.E_t_data.surface_solar_incidence_degs)
        ctx.F_conv_window = ctx.SHGC_angle > 0.5 ? 0.67 : 0.54
        ctx.Window_conv_mo_hr = ctx.eval('N*U*A*(t_out - t_in)*F_conv_window', {
          N: window.quantity.value,
        }, 'Window_conv_mo_hr')
        ctx.Window_rad_mo_hr = ctx.eval('N*U*A*(t_out - t_in)*(1 - F_conv_window)', {
          N: window.quantity.value,
        }, 'Window_rad_mo_hr')
        ctx.Window_loads_conv.set([mo, hr], ctx.Window_conv_mo_hr)
        ctx.Window_loads_rad.set([mo, hr], ctx.Window_rad_mo_hr);
        ctx.endSection()
      }
      ctx.endSection()
    }

    let res = {
      Window_nonsolar_conv: ctx.Window_loads_conv,
      Window_nonsolar_rad: ctx.Window_loads_rad,
    }
    ctx.logLoadMatrix('Nonsolar convective loads', ctx.Window_loads_conv)
    ctx.logLoadMatrix('Nonsolar radiative loads', ctx.Window_loads_rad)

    ctx.endSection()
    return res
  }

  /**
   * Used to calc Window solar loads.
   * This function is also used for door solar loads.
   * Can be passed a WallWindow or WallDoor
   */
  async calcWindowSolarLoad(wall, windowOrDoor) {
    let ctx = this.ctx;
    ctx.startSection(`${windowOrDoor.getTypeName()} Solar Load`)

    ctx.Window_loads = math.zeros(12, 24)
    let windowType = windowOrDoor.getWindowType()
    let optInShadingType = windowOrDoor.getInteriorShadingType()
    let iacCalculator = new IACCalculator(
      windowType,
      optInShadingType,
      gApp.proj().windowsData.iacValues,
    )
    ctx.SHGC = windowOrDoor.computeShgc()
    for (let mo = 0; mo < 12; ++mo) {
      ctx.startLocalSection(`Month ${mo}`)
      for (let hr = 0; hr < 24; ++hr) {
        ctx.startLocalSection(`Hour ${hr}`)
        //console.log(`Calculating ${windowOrDoor.getTypeName()} solar loads - Month ${mo}, Hour ${hr}`)
        ctx.setProgressText(`Calculating ${windowOrDoor.getTypeName()} solar loads - Month ${mo}, Hour ${hr}`)
        await ctx.briefWait()
        ctx.E_t_data = this.calc_solar_radiation(wall, mo, hr)
        if (!ctx.E_t_data.isSunVisibleInSky()) {
          ctx.log("Sun is not visible in sky at this hour. Solar load is 0.")
          ctx.endSection();
          continue;
        }
        ctx.SHGC_angle = this.calcSHGCForAngle(ctx.SHGC, ctx.E_t_data.surface_solar_incidence_degs)
        if (optInShadingType !== null) {
          ctx.IAC_res = iacCalculator.computeIACForSolarPosition(ctx, ctx.E_t_data)
        } else {
          ctx.IAC_res = {iac: 1, iacDiff: 1}
        }
        let shadedAreasCalculator = new ShadedAreasCalculator(ctx);
        let shadedAreas = shadedAreasCalculator.calcWindowShadedAreas(windowOrDoor, ctx.E_t_data, hr)
        ctx.Window_mo_hr = ctx.eval(
          'N*(E_t_b*SHGC_angle*(A_direct_clear + A_direct_shaded*IAC)' +
          ' + (E_t_d+E_t_r)*SHGC_D*(A_diffuse_clear + A_diffuse_shaded*IAC_D))', {
            N: windowOrDoor.quantity.value,
            E_t_b: ctx.E_t_data.E_t_b,
            E_t_d: ctx.E_t_data.E_t_d,
            E_t_r: ctx.E_t_data.E_t_r,
            SHGC_D: ctx.SHGC.Diffuse,
            IAC: ctx.IAC_res.iac,
            IAC_D: ctx.IAC_res.iacDiff,
            A_direct_clear: shadedAreas.A_direct_clear,
            A_direct_shaded: shadedAreas.A_direct_shaded,
            A_diffuse_clear: shadedAreas.A_diffuse_clear,
            A_diffuse_shaded: shadedAreas.A_diffuse_shaded,
        }, 'Window_mo_hr')
        ctx.Window_loads.set([mo, hr], ctx.Window_mo_hr)
        ctx.endSection()
      }
      ctx.endSection()
    }

    let res = ctx.Window_loads
    ctx.logLoadMatrix('Solar loads', ctx.Window_loads)

    ctx.endSection()
    return res
  }

  async calcWindowSensibleLoads() {
    let ctx = this.ctx;
    ctx.startSection("Windows")

    ctx.Windows_sensible = math.zeros(12, 24)
    ctx.Windows_solar = math.zeros(12, 24)
    ctx.Windows_nonsolar = math.zeros(12, 24)
    let breakdown = {}
    let walls = this.space.walls;
    
    for (let wallIndex = 0; wallIndex < walls.length; ++wallIndex) {
      let wall = walls[wallIndex];
      for (let winIndex = 0; winIndex < wall.windows.length; ++winIndex) {
        let window = wall.windows[winIndex];
        ctx.startLocalSection(`Wall ${wallIndex} - Window ${winIndex}`)

        // Nonsolar
        let nonSolarLoadsRes = await this.calcWindowNonSolarLoad(wall, window);
        ctx.Window_conv = nonSolarLoadsRes.Window_nonsolar_conv;
        ctx.Window_rad_nonsolar = nonSolarLoadsRes.Window_nonsolar_rad;
        ctx.Window_rad_nonsolar = this.applyRTSTransform(ctx.Window_rad_nonsolar, RadiantLoadType.NonSolar)
        ctx.Window_nonsolar = MatrixUtils.sumOfMatrices(
          [ctx.Window_conv, ctx.Window_rad_nonsolar])

        // Solar
        ctx.Window_rad_solar = await this.calcWindowSolarLoad(wall, window)
        ctx.Window_rad_solar = this.applyRTSTransform(ctx.Window_rad_solar, RadiantLoadType.Solar)

        ctx.Window_sensible = MatrixUtils.sumOfMatrices(
          [ctx.Window_conv, ctx.Window_rad_nonsolar, ctx.Window_rad_solar])

        ctx.Windows_sensible = math.add(ctx.Windows_sensible, ctx.Window_sensible)
        ctx.Windows_solar = math.add(ctx.Windows_solar, ctx.Window_rad_solar)
        ctx.Windows_nonsolar = math.add(ctx.Windows_nonsolar, ctx.Window_nonsolar)

        // Add to breakdown
        let windowType = window.getWindowType();
        let key = `Window${wallIndex+1}_${winIndex+1}`;
        let quantityStr = window.quantity.value > 1 ? ` x${window.quantity.value}` : '';
        let uValue = windowType.computeUValue().uValue
        let area = windowType.getArea()
        let shgc = windowType.computeShgc().Deg0
        breakdown[key] = {
          label: `Wall ${wallIndex + 1}, Window ${winIndex + 1}: ${windowType.name.value}${quantityStr}`,
          value: ctx.Window_sensible,
          metadata: {
            uValue: uValue,
            area: area * window.quantity.value,
            shgc: shgc,
          },
          children: {
            solar: {
              label: `Solar`,
              value: ctx.Window_rad_solar,
            },
            nonSolar: {
              label: `Non-Solar (Convective)`,
              value: ctx.Window_nonsolar,
            },
          }
        }

        ctx.logLoadMatrix('Window_conv', ctx.Window_conv)
        ctx.logLoadMatrix('Window_rad_nonsolar', ctx.Window_rad_nonsolar)
        ctx.logLoadMatrix('Window_rad_solar', ctx.Window_rad_solar);
        ctx.logLoadMatrix('Window_sensible', ctx.Window_sensible)

        ctx.endSection()
      }
    }

    ctx.log("Results:")
    ctx.logLoadMatrix('Windows_sensible', ctx.Windows_sensible)

    let res = {
      SensibleLoadsMatrix: ctx.Windows_sensible,
      SolarLoadsMatrix: ctx.Windows_solar,
      NonSolarLoadsMatrix: ctx.Windows_nonsolar,
      Breakdown: breakdown,
    }

    ctx.endSection()
    return res
  }

  async calcDoorNonSolarLoad(wall, door) {
    let ctx = this.ctx;
    ctx.startSection("Door Nonsolar Load")

    ctx.Door_loads_conv = math.zeros(12, 24)
    ctx.Door_loads_rad = math.zeros(12, 24)

    let doorType = door.getDoorType()
    let uValue = doorType.computeUValue()
    ctx.U_glass = uValue.uValueGlass
    ctx.U_opaq = uValue.uValueDoor
    ctx.A_glass = doorType.getGlassArea()
    ctx.A_opaq = doorType.getOpaqueArea()
    ctx.SHGC = doorType.computeShgc();
    ctx.doorAlpha = lookupData(DoorAlphaMap, [doorType.colour.value])
    for (let mo = 0; mo < 12; ++mo) {
      ctx.startLocalSection(`Month ${mo}`)
      for (let hr = 0; hr < 24; ++hr) {
        ctx.startLocalSection(`Hour ${hr}`)
        ctx.setProgressText(`Calculating door nonsolar loads - Month ${mo}, Hour ${hr}`)
        await ctx.briefWait()
        ctx.t_in = this.getSummerIndoorTemp(hr)
        ctx.t_out = this.getSummerOutdoorTemp(mo, hr)
        ctx.t_e = this.calc_t_e(wall, mo, hr, ctx.t_out, ctx.doorAlpha)
        ctx.E_t_data = this.calc_solar_radiation(wall, mo, hr)

        ctx.F_conv_wall = 0.54
        ctx.Door_mo_hr_glass = ctx.eval('N*U_glass*A_glass*(t_out - t_in)', {
          N: door.quantity.value,
        }, 'Door_mo_hr_glass')
        ctx.Door_mo_hr_opaq = ctx.eval('N*U_opaq*A_opaq*(t_e - t_in)', {
          N: door.quantity.value,
        }, 'Door_mo_hr_opaq')

        // Note: the door does not have a separate SHGC for glass and frame.
        ctx.SHGC_angle = this.calcSHGCForAngle(ctx.SHGC, ctx.E_t_data.surface_solar_incidence_degs)
        ctx.F_conv_glass = ctx.SHGC_angle > 0.5 ? 0.67 : 0.54;
        ctx.Door_conv_mo_hr = ctx.eval('Door_mo_hr_glass*F_conv_glass + Door_mo_hr_opaq*F_conv_wall', {
        }, 'Door_conv_mo_hr')
        ctx.Door_rad_nonsolar_mo_hr = ctx.eval('Door_mo_hr_glass*(1.0-F_conv_glass) + Door_mo_hr_opaq*(1.0-F_conv_wall)', {
        }, 'Door_rad_nonsolar_mo_hr')

        ctx.Door_loads_conv.set([mo, hr], ctx.Door_conv_mo_hr)
        ctx.Door_loads_rad.set([mo, hr], ctx.Door_rad_nonsolar_mo_hr)

        ctx.endSection()
      }
      ctx.endSection()
    }

    let res = {
      Door_nonsolar_conv: ctx.Door_loads_conv,
      Door_nonsolar_rad: ctx.Door_loads_rad,
    }
    ctx.logLoadMatrix('Nonsolar convective loads', ctx.Door_loads_conv)
    ctx.logLoadMatrix('Nonsolar radiative loads', ctx.Door_loads_rad)

    ctx.endSection()
    return res
  }

  async calcDoorSolarLoad(wall, door) {
    let ctx = this.ctx;
    ctx.startSection("Door Solar Load")

    let res = null
    let doorType = door.getDoorType()
    let percentGlass = doorType.getPercentGlass()
    if (percentGlass > 0) {
      res = await this.calcWindowSolarLoad(wall, door)
    } else {
      // No glass, skip calculations
      res = math.zeros(12, 24)
    }

    ctx.endSection()
    return res
  }

  async calcDoorSensibleLoads() {
    let ctx = this.ctx;
    ctx.startSection("Doors")

    ctx.Doors_sensible = math.zeros(12, 24)
    let breakdown = {}

    let walls = this.space.walls;
    for (let wallIndex = 0; wallIndex < walls.length; ++wallIndex) {
      let wall = walls[wallIndex];
      for (let doorIndex = 0; doorIndex < wall.doors.length; ++doorIndex) {
        let door = wall.doors[doorIndex];
        ctx.startLocalSection(`Wall ${wallIndex} - Door ${doorIndex}`)

        // Nonsolar
        let nonSolarLoadsRes = await this.calcDoorNonSolarLoad(wall, door)
        ctx.Door_conv = nonSolarLoadsRes.Door_nonsolar_conv;
        ctx.Door_rad_nonsolar = nonSolarLoadsRes.Door_nonsolar_rad;
        ctx.Door_rad_nonsolar = this.applyRTSTransform(ctx.Door_rad_nonsolar, RadiantLoadType.NonSolar)

        // Solar
        ctx.Door_rad_solar = await this.calcDoorSolarLoad(wall, door)
        ctx.Door_rad_solar = this.applyRTSTransform(ctx.Door_rad_solar, RadiantLoadType.Solar)

        ctx.Door_sensible = MatrixUtils.sumOfMatrices(
          [ctx.Door_conv, ctx.Door_rad_nonsolar, ctx.Door_rad_solar])
        ctx.Doors_sensible = math.add(ctx.Doors_sensible, ctx.Door_sensible)

        // Add to breakdown
        let doorType = door.getDoorType();
        let key = `Door${wallIndex+1}_${doorIndex+1}`;
        let quantityStr = door.quantity.value > 1 ? ` x${door.quantity.value}` : '';
        let uValue = doorType.computeUValue().uValue
        let area = doorType.getArea()
        let shgc = doorType.computeShgc().Deg0
        breakdown[key] = {
          label: `Wall ${wallIndex + 1}, Door ${doorIndex + 1}: ${doorType.name.value}${quantityStr}`,
          value: ctx.Door_sensible,
          metadata: {
            uValue: uValue,
            area: area * door.quantity.value,
            shgc: shgc,
          },
          children: {
            solar: {
              label: `Solar`,
              value: ctx.Door_rad_solar,
            },
            nonSolar: {
              label: `Non-Solar (Convective)`,
              value: MatrixUtils.sumOfMatrices([ctx.Door_conv, ctx.Door_rad_nonsolar]),
            },
          }
        }

        ctx.logLoadMatrix('Door_conv', ctx.Door_conv)
        ctx.logLoadMatrix('Door_rad_nonsolar', ctx.Door_rad_nonsolar)
        ctx.logLoadMatrix('Door_rad_solar', ctx.Door_rad_solar);
        ctx.logLoadMatrix('Door_sensible', ctx.Door_sensible)

        ctx.endSection()
      }
    }

    ctx.log("Results:")
    ctx.logLoadMatrix('Doors_sensible', ctx.Doors_sensible)

    let res = {
      SensibleLoadsMatrix: ctx.Doors_sensible,
      Breakdown: breakdown,
    }

    ctx.endSection()
    return res
  }

  async calcSkylightNonSolarLoad(roof, skylight) {
    let ctx = this.ctx;
    ctx.startSection("Skylight Nonsolar Load")

    ctx.Skylight_loads_conv = math.zeros(12, 24)
    ctx.Skylight_loads_rad = math.zeros(12, 24)

    let skylightType = skylight.getSkylightType()
    ctx.U = skylightType.computeUValue().uValue
    ctx.A = skylightType.getArea()
    ctx.SHGC = skylightType.computeShgc();
    for (let mo = 0; mo < 12; ++mo) {
      ctx.startLocalSection(`Month ${mo}`)
      for (let hr = 0; hr < 24; ++hr) {
        ctx.startLocalSection(`Hour ${hr}`)
        ctx.setProgressText(`Calculating skylight nonsolar loads - Month ${mo}, Hour ${hr}`)
        await ctx.briefWait()

        ctx.t_in = this.getSummerIndoorTemp(hr)
        ctx.t_out = this.getSummerOutdoorTemp(mo, hr)
        ctx.E_t_data = this.calc_solar_radiation(roof, mo, hr)
        ctx.SHGC_angle = this.calcSHGCForAngle(ctx.SHGC, ctx.E_t_data.surface_solar_incidence_degs)
        ctx.F_conv_window = ctx.SHGC_angle > 0.5 ? 0.67 : 0.54

        ctx.Skylight_conv_mo_hr = ctx.eval('N*U*A*(t_out - t_in)*F_conv_window', {
          N: skylight.quantity.value,
        }, 'Skylight_conv_mo_hr')
        ctx.Skylight_rad_mo_hr = ctx.eval('N*U*A*(t_out - t_in)*(1 - F_conv_window)', {
          N: skylight.quantity.value,
        }, 'Skylight_rad_mo_hr')

        ctx.Skylight_loads_conv.set([mo, hr], ctx.Skylight_conv_mo_hr)
        ctx.Skylight_loads_rad.set([mo, hr], ctx.Skylight_rad_mo_hr)
        ctx.endSection()
      }
      ctx.endSection()
    }

    let res = {
      Skylight_nonsolar_conv: ctx.Skylight_loads_conv,
      Skylight_nonsolar_rad: ctx.Skylight_loads_rad,
    };
    ctx.logLoadMatrix('Nonsolar convective loads', ctx.Skylight_loads_conv)
    ctx.logLoadMatrix('Nonsolar radiative loads', ctx.Skylight_loads_rad)

    ctx.endSection()
    return res
  }

  calcSHGCForAngle(shgcs, surface_solar_incidence_degs) {
    /**
     * Given a theta for some mo, hr (computed from solar calculations), lerp to get
     * a SHGC value for that angle.
     */
    let ctx = this.ctx;
    ctx.startSection(`SHGC for time`)
    if (shgcs.isNA) {
      ctx.log("SHGC is NA. Returning 0")
      ctx.endSection()
      return 0;
    }
    ctx.log(`Lerping SHGC for angle ${surface_solar_incidence_degs}`)
    ctx.assert(surface_solar_incidence_degs >= 0 && surface_solar_incidence_degs <= 360, `Invalid theta: ${surface_solar_incidence_degs} (expected 0-360)`)
    if (surface_solar_incidence_degs >= 90) {
      ctx.log("SHGC is 0 for theta >= 90")
      ctx.SHGC_angle = 0;
    } else {
      ctx.lerpMap = {
        0: shgcs.Deg0,
        40: shgcs.Deg40,
        50: shgcs.Deg50,
        60: shgcs.Deg60,
        70: shgcs.Deg70,
        80: shgcs.Deg80,
        90: 0.0,
      };
      ctx.SHGC_angle = interpolateInMap(ctx.lerpMap, surface_solar_incidence_degs)
    }
    let res = ctx.SHGC_angle
    ctx.endSection()
    return res
  }

  async calcSkylightSolarLoad(roof, skylight) {
    let ctx = this.ctx;
    ctx.startSection("Skylight Solar Load")

    ctx.Skylight_loads = math.zeros(12, 24)
    let skylightType = skylight.getSkylightType()
    ctx.SHGC = skylightType.computeShgc()
    for (let mo = 0; mo < 12; ++mo) {
      ctx.startLocalSection(`Month ${mo}`)
      for (let hr = 0; hr < 24; ++hr) {
        ctx.startLocalSection(`Hour ${hr}`)
        ctx.setProgressText(`Calculating skylight solar loads - Month ${mo}, Hour ${hr}`)
        await ctx.briefWait()
        ctx.E_t_data = this.calc_solar_radiation(roof, mo, hr)
        if (!ctx.E_t_data.isSunVisibleInSky()) {
          ctx.log("Sun is not visible in sky at this hour. Solar load is 0.")
          ctx.endSection();
          continue;
        }
        ctx.SHGC_angle = this.calcSHGCForAngle(ctx.SHGC, ctx.E_t_data.surface_solar_incidence_degs)
        ctx.Skylight_mo_hr = ctx.eval('N*E_t*SHGC_angle', {
          N: skylight.quantity.value,
          E_t: ctx.E_t_data.E_t,
        }, 'Skylight_mo_hr')
        ctx.Skylight_loads.set([mo, hr], ctx.Skylight_mo_hr)
        ctx.endSection()
      }
      ctx.endSection()
    }

    let res = ctx.Skylight_loads
    ctx.logLoadMatrix('Solar loads', ctx.Skylight_loads)

    ctx.endSection()
    return res
  }

  async calcSkylightSensibleLoads() {
    let ctx = this.ctx;
    ctx.startSection("Skylights")

    ctx.Skylights_sensible = math.zeros(12, 24)
    ctx.Skylights_solar = math.zeros(12, 24)
    ctx.Skylights_nonsolar = math.zeros(12, 24)
    let breakdown = {}

    let roofs = this.space.roofs;
    for (let roofIndex = 0; roofIndex < roofs.length; ++roofIndex) {
      let roof = roofs[roofIndex];
      for (let skylightIndex = 0; skylightIndex < roof.skylights.length; ++skylightIndex) {
        let skylight = roof.skylights[skylightIndex];
        ctx.startLocalSection(`Roof ${roofIndex} - Skylight ${skylightIndex}`)

        // Nonsolar
        let nonSolarLoadsRes = await this.calcSkylightNonSolarLoad(roof, skylight)
        ctx.Skylight_conv = nonSolarLoadsRes.Skylight_nonsolar_conv;
        ctx.Skylight_rad_nonsolar = nonSolarLoadsRes.Skylight_nonsolar_rad;
        ctx.Skylight_rad_nonsolar = this.applyRTSTransform(ctx.Skylight_rad_nonsolar, RadiantLoadType.NonSolar)
        ctx.Skylight_nonsolar = MatrixUtils.sumOfMatrices(
          [ctx.Skylight_conv, ctx.Skylight_rad_nonsolar])

        // Solar
        ctx.Skylight_rad_solar = await this.calcSkylightSolarLoad(roof, skylight)
        ctx.Skylight_rad_solar = this.applyRTSTransform(ctx.Skylight_rad_solar, RadiantLoadType.Solar)

        ctx.Skylight_sensible = MatrixUtils.sumOfMatrices(
          [ctx.Skylight_conv, ctx.Skylight_rad_nonsolar, ctx.Skylight_rad_solar])
        ctx.Skylights_sensible = math.add(ctx.Skylights_sensible, ctx.Skylight_sensible)
        ctx.Skylights_solar = math.add(ctx.Skylights_solar, ctx.Skylight_rad_solar)
        ctx.Skylights_nonsolar = math.add(ctx.Skylights_nonsolar, ctx.Skylight_nonsolar)

        // Add to breakdown
        let skylightType = skylight.getSkylightType();
        let key = `Skylight${roofIndex+1}_${skylightIndex+1}`;
        let quantityStr = skylight.quantity.value > 1 ? ` x${skylight.quantity.value}` : '';
        let uValue = skylightType.computeUValue().uValue
        let area = skylightType.getArea()
        let shgc = skylightType.computeShgc().Deg0;
        breakdown[key] = {
          label: `Roof ${roofIndex + 1}, Skylight ${skylightIndex + 1}: ${skylightType.name.value}${quantityStr}`,
          value: ctx.Skylight_sensible,
          metadata: {
            uValue: uValue,
            area: area * skylight.quantity.value,
            shgc: shgc,
          },
          children: {
            solar: {
              label: `Solar`,
              value: ctx.Skylight_rad_solar,
            },
            nonSolar: {
              label: `Non-Solar (Convective)`,
              value: ctx.Skylight_nonsolar,
            },
          }
        }

        ctx.logLoadMatrix('Skylight_conv', ctx.Skylight_conv)
        ctx.logLoadMatrix('Skylight_rad_nonsolar', ctx.Skylight_rad_nonsolar)
        ctx.logLoadMatrix('Skylight_rad_solar', ctx.Skylight_rad_solar);
        ctx.logLoadMatrix('Skylight_sensible', ctx.Skylight_sensible)

        ctx.endSection()
      }
    }

    ctx.log("Results:")
    ctx.logLoadMatrix('Skylights_sensible', ctx.Skylights_sensible)

    let res = {
      SensibleLoadsMatrix: ctx.Skylights_sensible,
      SolarLoadsMatrix: ctx.Skylights_solar,
      NonSolarLoadsMatrix: ctx.Skylights_nonsolar,
      Breakdown: breakdown,
    }

    ctx.endSection()
    return res
  }

  async calcApplianceSensibleLoads() {
    let ctx = this.ctx;
    ctx.setProgressText("Calculating appliance sensible loads")
    ctx.startSection(`Appliance Sensible Loads`)

    let appliances = this.space.internals.appliances;
    let breakdown = {}

    ctx.Appliances_sensible = math.zeros(12, 24)

    for (let i = 0; i < appliances.appliances.length; ++i) {
      let appliance = appliances.appliances[i];
      ctx.startLocalSection(`Appliance ${i}`)
      let applianceData = appliance.getApplianceData(ctx)
      ctx.S_app = applianceData.S_app
      ctx.F_conv = applianceData.F_conv
      ctx.F_rad = applianceData.F_rad
      ctx.appSched = appliance.getSchedule().getData()
      ctx.N = appliance.quantity.value
      ctx.A_conv_row = makeVector(24)
      ctx.A_rad_row = makeVector(24)
      for (let hr = 0; hr < 24; hr++) {
        ctx.startLocalSection(`Hour ${hr}`)
        ctx.conv_appliance = ctx.eval('N*S_app*sched*F_conv', {
          sched: ctx.appSched[hr],
        }, 'conv_appliance')
        ctx.rad_appliance = ctx.eval('N*S_app*sched*F_rad', {
          sched: ctx.appSched[hr],
        }, 'rad_appliance')

        ctx.A_conv_row[hr] = ctx.conv_appliance
        ctx.A_rad_row[hr] = ctx.rad_appliance
        ctx.endSection()
      }
      // Create matrices from the row
      ctx.A_conv = MatrixUtils.createMatrixFromRow(ctx.A_conv_row, 12)
      ctx.A_rad = MatrixUtils.createMatrixFromRow(ctx.A_rad_row, 12)
      ctx.A_rad = this.applyRTSTransform(ctx.A_rad, RadiantLoadType.NonSolar)

      ctx.A_sensible = MatrixUtils.sumOfMatrices([ctx.A_conv, ctx.A_rad])
      ctx.Appliances_sensible = math.add(ctx.Appliances_sensible, ctx.A_sensible)

      // Add to breakdown
      let key = `Appliance${i+1}`;
      let quantityStr = ctx.N > 1 ? ` x${ctx.N}` : '';
      breakdown[key] = {
        label: `Appliance ${i + 1}${quantityStr}`,
        value: ctx.A_sensible,
      }

      ctx.logLoadMatrix('A_conv', ctx.A_conv)
      ctx.logLoadMatrix('A_rad', ctx.A_rad)
      ctx.logLoadMatrix('A_sensible', ctx.A_sensible)

      ctx.endSection()
    }

    ctx.log("Results:")
    ctx.logLoadMatrix('Appliances_sensible', ctx.Appliances_sensible)

    let res = {
      SensibleLoadsMatrix: ctx.Appliances_sensible,
      Breakdown: breakdown,
    }

    ctx.endSection()
    return res
  }

  async calcLightingSensibleLoads() {
    let ctx = this.ctx;
    ctx.setProgressText("Calculating lighting sensible loads")
    ctx.startSection(`Lighting Sensible Loads`)
    let lighting = this.space.internals.lighting;

    ctx.A_sensible_lights = math.zeros(12, 24)
    ctx.A_plenum_lights = math.zeros(12, 24)

    ctx.L = lighting.getPower()
    let lightingFractions = lighting.getRadiantAndSpaceFractions()
    ctx.F_rad = lightingFractions.F_rad
    ctx.F_space = lightingFractions.F_space
    let sched = lighting.getSchedule().getData()
    ctx.A_conv_lights_row = makeVector(24)
    ctx.A_rad_lights_row = makeVector(24)
    ctx.A_plenum_lights_row = makeVector(24)
    for (let hr = 0; hr < 24; hr++) {
      ctx.startLocalSection(`Hour ${hr}`)
      ctx.conv_load = ctx.eval('L*sched*(1-F_rad)*F_space', {
        sched: sched[hr],
      }, 'conv_load')
      ctx.rad_load = ctx.eval('L*sched*F_rad*F_space', {
        sched: sched[hr],
      }, 'rad_load')
      ctx.plenum_load = ctx.eval('L*sched*(1-F_space)', {
        sched: sched[hr],
      }, 'plenum_load')
      ctx.A_conv_lights_row[hr] = ctx.conv_load
      ctx.A_rad_lights_row[hr] = ctx.rad_load
      ctx.A_plenum_lights_row[hr] = ctx.plenum_load
      ctx.endSection()
    }

    ctx.A_conv_lights = MatrixUtils.createMatrixFromRow(ctx.A_conv_lights_row, 12)
    ctx.A_rad_lights = MatrixUtils.createMatrixFromRow(ctx.A_rad_lights_row, 12)
    ctx.A_plenum_lights = MatrixUtils.createMatrixFromRow(ctx.A_plenum_lights_row, 12)

    ctx.A_rad_lights = this.applyRTSTransform(ctx.A_rad_lights, RadiantLoadType.NonSolar)
    // Note: the plenum loads are kept separate from the sensible loads
    ctx.A_sensible = MatrixUtils.sumOfMatrices([ctx.A_conv_lights, ctx.A_rad_lights])

    ctx.log("Results:")
    ctx.logLoadMatrix('A_conv_lights', ctx.A_conv_lights)
    ctx.logLoadMatrix('A_rad_lights', ctx.A_rad_lights)
    ctx.logLoadMatrix('A_plenum_lights', ctx.A_plenum_lights)
    ctx.logLoadMatrix('A_sensible', ctx.A_sensible)

    let breakdown = {
      "Lighting": {
        label: "Lighting Sensible Load",
        value: ctx.A_sensible,
      }
    };

    let res = {
      SensibleLoadsMatrix: ctx.A_sensible,
      PlenumLoadsMatrix: ctx.A_plenum_lights,
      Breakdown: breakdown,
    }

    ctx.endSection()
    return res
  }

  async calcPeopleSensibleLoads() {
    let ctx = this.ctx;
    ctx.setProgressText("Calculating people sensible loads")
    ctx.startSection(`People Sensible Loads`)
    let people = this.space.internals.people;

    ctx.A_conv_people_row = makeVector(24)
    ctx.A_rad_people_row = makeVector(24)

    ctx.M = people.getNumOccupants().result
    ctx.activityLevel = people.getActivityLevel()
    let activityLevelData = people.getActivityLevelData(ctx)
    ctx.S_P = activityLevelData.S_P
    ctx.F_rad = activityLevelData.F_rad
    let sched = people.getSchedule().getData()
    for (let hr = 0; hr < 24; hr++) {
      ctx.startLocalSection(`Hour ${hr}`)
      ctx.conv_load = ctx.eval('S_P*sched*M*(1.0 - F_rad)', {
        sched: sched[hr],
      }, 'conv_load')
      ctx.rad_load = ctx.eval('S_P*sched*M*F_rad', {
        sched: sched[hr],
      }, 'rad_load')
      ctx.A_conv_people_row[hr] = ctx.conv_load
      ctx.A_rad_people_row[hr] = ctx.rad_load
      ctx.endSection()
    }

    ctx.A_conv_people = math.zeros(12, 24)
    ctx.A_rad_people = math.zeros(12, 24)
    for (let mo = 0; mo < 12; mo++) {
      MatrixUtils.setRow(ctx.A_conv_people, mo, ctx.A_conv_people_row)
      MatrixUtils.setRow(ctx.A_rad_people, mo, ctx.A_rad_people_row)
    }

    ctx.A_rad_people = this.applyRTSTransform(ctx.A_rad_people, RadiantLoadType.NonSolar)
    ctx.A_sensible = MatrixUtils.sumOfMatrices([ctx.A_conv_people, ctx.A_rad_people])

    ctx.log("Results:")
    ctx.logLoadMatrix('A_conv_people', ctx.A_conv_people)
    ctx.logLoadMatrix('A_rad_people', ctx.A_rad_people)
    ctx.logLoadMatrix('A_sensible', ctx.A_sensible)

    let breakdown = {
      "People": {
        label: "People Sensible Load",
        value: ctx.A_sensible,
      }
    };

    let res = {
      SensibleLoadsMatrix: ctx.A_sensible,
      Breakdown: breakdown,
    }

    ctx.endSection()
    return res
  }

  async calcMiscSensibleLoads() {
    let ctx = this.ctx;
    ctx.setProgressText("Calculating miscellaneous sensible loads")
    ctx.startSection(`Miscellaneous Sensible Loads`)

    let miscLoads = this.space.internals.miscLoads;
    let breakdown = {};

    let M_sens_rad = math.zeros(12, 24)
    let M_sens_conv = math.zeros(12, 24)

    let M_sens_rad_row = makeVector(24)
    let M_sens_conv_row = makeVector(24)

    ctx.M_sens = miscLoads.getSensibleLoad(ctx)
    ctx.F_rad = miscLoads.getSensibleLoadRadiantFraction()
    let sched = miscLoads.getSchedule().getData()
    for (let hr = 0; hr < 24; hr++) {
      ctx.startLocalSection(`Hour ${hr}`)
      ctx.sens_rad = ctx.eval('M_sens*F_rad*sched', {
        sched: sched[hr],
      }, 'sens_rad')
      ctx.sens_conv = ctx.eval('M_sens*(1 - F_rad)*sched', {
        sched: sched[hr],
      }, 'sens_conv')
      M_sens_rad_row[hr] = ctx.sens_rad
      M_sens_conv_row[hr] = ctx.sens_conv
      ctx.endSection()
    }

    for (let mo = 0; mo < 12; mo++) {
      MatrixUtils.setRow(M_sens_rad, mo, M_sens_rad_row)
      MatrixUtils.setRow(M_sens_conv, mo, M_sens_conv_row)
    }

    ctx.M_sens_rad = this.applyRTSTransform(M_sens_rad, RadiantLoadType.NonSolar)
    ctx.M_sensible = MatrixUtils.sumOfMatrices([M_sens_rad, M_sens_conv])

    ctx.log("Results:")
    ctx.logLoadMatrix('M_sens_rad', M_sens_rad)
    ctx.logLoadMatrix('M_sens_conv', M_sens_conv)
    ctx.logLoadMatrix('M_sensible', ctx.M_sensible)

    breakdown["Misc"] = {
      label: "Miscellaneous Sensible Load",
      value: ctx.M_sensible,
    }

    let res = {
      SensibleLoadsMatrix: ctx.M_sensible,
      Breakdown: breakdown,
    }

    ctx.endSection()
    return res
  }

  async calcInfiltrationSensibleLoads() {
    let ctx = this.ctx;
    ctx.setProgressText("Calculating infiltration sensible loads")
    ctx.startSection(`Infiltration Sensible Loads`)

    ctx.A_sens_inf = math.zeros(12, 24)

    ctx.occupancySchedule = makeVector(24).fill(1.0);
    ctx.C_s = ctx.call(adjustForAltitude, 1.08, ctx.P_loc)
    for (let mo = 0; mo < 12; mo++) {
      ctx.startLocalSection(`Month ${mo}`)
      ctx.Q_inf = this.space.calcInfiltrationFlowRate(ctx, getSeasonOfMonth(mo)).Q_inf;
      for (let hr = 0; hr < 24; hr++) {
        ctx.startLocalSection(`Hour ${hr}`)
        ctx.t_in = this.getSummerIndoorTemp(hr)
        ctx.t_out = this.getSummerOutdoorTemp(mo, hr)
        ctx.sched = ctx.occupancySchedule[hr] > 0 ? 1 : 0
        ctx.sens_inf = ctx.eval('Q_inf*sched*C_s*(t_out - t_in)', {
        }, 'sens_inf')
        ctx.A_sens_inf.set([mo, hr], ctx.sens_inf)
        ctx.endSection()
      }
      ctx.endSection()
    }

    // Note - do not apply RTS transform here. Loads are purely convective.
    ctx.log("Results:")
    ctx.logLoadMatrix('A_sens_inf', ctx.A_sens_inf)

    let breakdown = {
      "Infiltration": {
        label: "Infiltration Sensible Load",
        value: ctx.A_sens_inf,
      }
    };

    let res = {
      SensibleLoadsMatrix: ctx.A_sens_inf,
      Breakdown: breakdown,
    }

    ctx.endSection()
    return res
  }

  async calcPartitionNonSolarLoads(partition) {
    let ctx = this.ctx;
    ctx.startSection("Partition Nonsolar Loads")

    ctx.Partition_nonsolar_conv = math.zeros(12, 24)
    ctx.Partition_nonsolar_rad = math.zeros(12, 24)

    for (let mo = 0; mo < 12; ++mo) {
      ctx.startLocalSection(`Month ${mo}`)
      for (let hr = 0; hr < 24; ++hr) {
        ctx.startLocalSection(`Hour ${hr}`)
        ctx.setProgressText(`Calculating partition nonsolar loads - Month ${mo}, Hour ${hr}`)
        await ctx.briefWait()

        ctx.log("Using F_conv for walls")
        ctx.F_conv = 0.54
        ctx.t_i = this.getSummerIndoorTemp(hr)
        ctx.t_o = this.getSummerOutdoorTemp(mo, hr)
        ctx.Partition_load_mo_hr = partition._calcLoads(ctx, false, {t_i: ctx.t_i, t_o: ctx.t_o})
        ctx.nonsolar_conv_mo_hr = ctx.eval('Partition_load_mo_hr*F_conv', {
        }, 'nonsolar_conv_mo_hr')
        ctx.nonsolar_rad_mo_hr = ctx.eval('Partition_load_mo_hr*(1.0 - F_conv)', {
        }, 'nonsolar_rad_mo_hr')
        ctx.Partition_nonsolar_conv.set([mo, hr], ctx.nonsolar_conv_mo_hr)
        ctx.Partition_nonsolar_rad.set([mo, hr], ctx.nonsolar_rad_mo_hr)

        ctx.endSection()
      }
      ctx.endSection()
    }

    ctx.logLoadMatrix('Partition nonsolar convective loads', ctx.Partition_nonsolar_conv)
    ctx.logLoadMatrix('Partition nonsolar radiative loads', ctx.Partition_nonsolar_rad)

    let res = {
      Partition_nonsolar_conv: ctx.Partition_nonsolar_conv,
      Partition_nonsolar_rad: ctx.Partition_nonsolar_rad,
    }
    ctx.endSection()
    return res
  }

  async calcPartitionSensibleLoads() {
    let ctx = this.ctx;
    ctx.setProgressText("Calculating partition sensible loads")
    ctx.startSection(`Partition Sensible Loads`)

    ctx.Partitions_sensible = math.zeros(12, 24)
    let breakdown = {}

    let partitions = this.space.partitions;
    for (let i = 0; i < partitions.length; ++i) {
      let partition = partitions[i];
      ctx.startLocalSection(`Partition ${i}`)
      let wallTypeName = partition.getWallType().name.value;
      let bufferSpaceTypeName = partition.getBufferSpaceType().name.value;
      ctx.log(`Wall type: ${wallTypeName}, Buffer space type: ${bufferSpaceTypeName}`)

      let nonSolarLoadsRes = await this.calcPartitionNonSolarLoads(partition)
      ctx.Partition_conv = nonSolarLoadsRes.Partition_nonsolar_conv;
      ctx.Partition_rad = nonSolarLoadsRes.Partition_nonsolar_rad;
      ctx.Partition_rad = this.applyRTSTransform(ctx.Partition_rad, RadiantLoadType.NonSolar)

      ctx.Partition_sensible = MatrixUtils.sumOfMatrices(
        [ctx.Partition_conv, ctx.Partition_rad])
      ctx.Partitions_sensible = math.add(ctx.Partitions_sensible, ctx.Partition_sensible)

      // Add to breakdown
      let key = `Partition${i+1}`;
      let partitionUValue = 1.0 / partition.getWallType().getRValue();
      breakdown[key] = {
        label: `Partition ${i + 1}: ${wallTypeName} (${partition.partitionType.getSelectLabelStr()})`,
        value: ctx.Partition_sensible,
        metadata: {
          uValue: partitionUValue,
          area: partition.getArea(),
        }
      }

      ctx.logLoadMatrix('Partition_conv', ctx.Partition_conv)
      ctx.logLoadMatrix('Partition_rad', ctx.Partition_rad)
      ctx.logLoadMatrix('Partition_sensible', ctx.Partition_sensible)

      ctx.endSection()
    }

    ctx.log("Results:")
    ctx.logLoadMatrix('Partition sensible loads', ctx.Partitions_sensible)

    let res = {
      SensibleLoadsMatrix: ctx.Partitions_sensible,
      Breakdown: breakdown,
    }

    ctx.endSection()
    return res
  }

  calcFloorLoad(floor, mo, hr) {
    let ctx = this.ctx;
    ctx.startSection("Floor load")
    ctx.t_i = this.getSummerIndoorTemp(hr)
    ctx.t_o = this.getSummerOutdoorTemp(mo, hr)
    // Calculations are already handled by the Floor class
    ctx.Floor_load_mo_hr = floor._calcLoads(ctx, false, {
      t_i: ctx.t_i, t_o: ctx.t_o
    }).q;
    let res = ctx.Floor_load_mo_hr
    ctx.endSection()
    return res
  }

  async calcFloorNonSolarLoads(floor) {
    let ctx = this.ctx;
    ctx.startSection("Floor Nonsolar Loads")

    ctx.Floor_nonsolar_conv = math.zeros(12, 24)
    ctx.Floor_nonsolar_rad = math.zeros(12, 24)

    for (let mo = 0; mo < 12; ++mo) {
      ctx.startLocalSection(`Month ${mo}`)
      for (let hr = 0; hr < 24; ++hr) {
        ctx.startLocalSection(`Hour ${hr}`)
        ctx.setProgressText(`Calculating floor nonsolar loads - Month ${mo}, Hour ${hr}`)
        await ctx.briefWait()

        ctx.log("Using F_conv for walls")
        ctx.F_conv = 0.54
        ctx.Floor_load_mo_hr = this.calcFloorLoad(floor, mo, hr)

        ctx.nonsolar_conv_mo_hr = ctx.eval('Floor_load_mo_hr*F_conv', {
        }, 'nonsolar_conv_mo_hr')
        ctx.nonsolar_rad_mo_hr = ctx.eval('Floor_load_mo_hr*(1.0 - F_conv)', {
        }, 'nonsolar_rad_mo_hr')
        ctx.Floor_nonsolar_conv.set([mo, hr], ctx.nonsolar_conv_mo_hr)
        ctx.Floor_nonsolar_rad.set([mo, hr], ctx.nonsolar_rad_mo_hr)

        ctx.endSection()
      }
      ctx.endSection()
    }

    ctx.logLoadMatrix('Floor nonsolar convective loads', ctx.Floor_nonsolar_conv)
    ctx.logLoadMatrix('Floor nonsolar radiative loads', ctx.Floor_nonsolar_rad)

    let res = {
      Floor_nonsolar_conv: ctx.Floor_nonsolar_conv,
      Floor_nonsolar_rad: ctx.Floor_nonsolar_rad,
    }
    ctx.endSection()
    return res
  }

  async calcFloorSensibleLoads() {
    let ctx = this.ctx;
    ctx.setProgressText("Calculating floor sensible loads")
    ctx.startSection(`Floor Sensible Loads`)

    ctx.Floors_sensible = math.zeros(12, 24)
    let breakdown = {}

    let floors = this.space.floors;
    for (let i = 0; i < floors.length; ++i) {
      let floor = floors[i];
      let floorType = floor.floorType.value;
      ctx.startLocalSection(`Floor ${i} - ${floorType}`)
      if (!(floorType == FloorType.FloorAboveCrawlSpace ||
        floorType == FloorType.FloorRaisedOffGround)) {
        ctx.log(`Skipping floor - type ${floorType} does not have cooling loads.`)
        ctx.endSection();
        continue;
      }

      let nonSolarLoadsRes = await this.calcFloorNonSolarLoads(floor)
      ctx.Floor_conv = nonSolarLoadsRes.Floor_nonsolar_conv;
      ctx.Floor_rad = nonSolarLoadsRes.Floor_nonsolar_rad;
      ctx.Floor_rad = this.applyRTSTransform(ctx.Floor_rad, RadiantLoadType.NonSolar)

      ctx.Floor_sensible = MatrixUtils.sumOfMatrices(
        [ctx.Floor_conv, ctx.Floor_rad])
      ctx.Floors_sensible = math.add(ctx.Floors_sensible, ctx.Floor_sensible)

      // Add to breakdown
      let key = `Floor${i+1}`;
      breakdown[key] = {
        label: `Floor ${i + 1}: ${floorType}`,
        value: ctx.Floor_sensible,
      }

      ctx.logLoadMatrix('Floor_conv', ctx.Floor_conv)
      ctx.logLoadMatrix('Floor_rad', ctx.Floor_rad)
      ctx.logLoadMatrix('Floor_sensible', ctx.Floor_sensible)

      ctx.endSection()
    }

    ctx.log("Results:")
    ctx.logLoadMatrix('Floor sensible loads', ctx.Floors_sensible)

    let res = {
      SensibleLoadsMatrix: ctx.Floors_sensible,
      Breakdown: breakdown,
    }

    ctx.endSection()
    return res
  }

  async calcSolarValues() {
    let ctx = this.ctx;
    ctx.startSection("Solar values")

    // Make a 12x24 matrix of objects
    let solarValuesMap = new MonthHourMap();

    let primarySurfaces = solar.getPrimarySurfacesMap();
    let locData = ctx.toplevelData.locationData;
    for (let mo = 0; mo < 12; ++mo) {
      ctx.startLocalSection(`Month ${mo}`)
      for (let hr = 0; hr < 24; ++hr) {
        ctx.startLocalSection(`Hour ${hr}`)
        ctx.setProgressText(`Calculating solar values - ${mo}, Hour ${hr}`)
        await ctx.briefWait()

        ctx.dayOfYear = ctx.eval('getDayOfYear(monthIndex, dayOfMonth)', {
          getDayOfYear: solar.getDayOfYear,
          monthIndex: mo,
          dayOfMonth: ctx.toplevelData.dayOfMonth,
        }, 'dayOfYear')
        ctx.clearSkyValues = ctx.call(solar.calc_clear_sky_solar_radiation, 
          locData.timezone,
          hr,
          ctx.dayOfYear,
          locData.latitude,
          locData.longitude, 
          locData.tauBValuesByMonth[mo],
          locData.tauDValuesByMonth[mo],
        );
        let primarySurfaceValues = {}
        for (let surfaceKey in primarySurfaces) {
          let primarySurface = primarySurfaces[surfaceKey];
          ctx.groundReflectance = ctx.buildingAndEnv.getReflectance(mo, primarySurface.surfaceDir);
          primarySurfaceValues[surfaceKey] = ctx.call(solar.apply_to_surface,
            ctx.clearSkyValues,
            primarySurface.surfaceDir,
            primarySurface.tiltAngleDegrees,
            ctx.groundReflectance,
          );
        }
        solarValuesMap.set(mo, hr, {
          clearSkyValues: ctx.clearSkyValues,
          primarySurfaceValues: primarySurfaceValues,
        })
        ctx.endSection()
      }
      ctx.endSection()
    }

    let res = solarValuesMap;
    ctx.endSection()
    return res;
  }

  async calcLatentLoads() {
    let ctx = this.ctx;
    ctx.startSection("Latent Loads")
    ctx.startProgressSection("Latent loads", {
      "Infiltration": {},
      "Appliances": {},
      "People": {},
      "Misc": {},
    })
    ctx.setProgress("Infiltration")
    let infiltrationLoads = await this.calcInfiltrationLatentLoads()
    await ctx.briefWait()
    ctx.setProgress("Appliances")
    let applianceLoads = await this.calcApplianceLatentLoads()
    await ctx.briefWait()
    ctx.setProgress("People")
    let peopleLoads = await this.calcPeopleLatentLoads()
    await ctx.briefWait()
    ctx.setProgress("Misc")
    let miscLoads = await this.calcMiscLatentLoads()
    await ctx.briefWait()

    let totalLoads = MatrixUtils.sumOfMatrices([
      infiltrationLoads.LatentLoadsMatrix,
      applianceLoads.LatentLoadsMatrix,
      peopleLoads.LatentLoadsMatrix,
      miscLoads.LatentLoadsMatrix,
    ])
    ctx.logLoadMatrix('Total latent loads', totalLoads)

    // Combine all breakdowns
    let combinedBreakdown = {
      label: 'Latent Loads',
      value: totalLoads,
      children: {
        "Infiltration": {
          label: "Infiltration",
          value: infiltrationLoads.LatentLoadsMatrix,
          children: infiltrationLoads.Breakdown,
        },
        "Appliances": {
          label: "Appliances",
          value: applianceLoads.LatentLoadsMatrix,
          children: applianceLoads.Breakdown,
        },
        "People": {
          label: "People",
          value: peopleLoads.LatentLoadsMatrix,
          children: peopleLoads.Breakdown,
        },
        "Misc": {
          label: "Miscellaneous",
          value: miscLoads.LatentLoadsMatrix,
          children: miscLoads.Breakdown,
        },
      }
    };

    let res = {
      totalLoads: totalLoads,
      Breakdown: combinedBreakdown,

      infiltrationLoads: infiltrationLoads,
      applianceLoads: applianceLoads,
      peopleLoads: peopleLoads,
      miscLoads: miscLoads,
    }

    ctx.endProgressSection();
    ctx.endSection()
    return res
  }

  async calcSensibleLoads() {
    let ctx = this.ctx;
    ctx.startSection("Sensible Loads")
    ctx.startProgressSection("Sensible loads", {
      "Walls": {},
      "Roofs": {},
      "Windows": {},
      "Doors": {},
      "Skylights": {},
      "Partitions": {},
      "Floors": {},
      "Appliances": {},
      "Lighting": {},
      "People": {},
      "Misc": {},
      "Infiltration": {},
    })
    ctx.setProgress("Walls")
    let wallLoads = await this.calcWallSensibleLoads(true)
    await ctx.briefWait()
    ctx.setProgress("Roofs")  
    let roofLoads = await this.calcWallSensibleLoads(false)
    await ctx.briefWait()
    ctx.setProgress("Windows")
    let windowLoads = await this.calcWindowSensibleLoads()
    await ctx.briefWait()
    ctx.setProgress("Doors")
    let doorLoads = await this.calcDoorSensibleLoads()
    await ctx.briefWait()
    ctx.setProgress("Skylights")
    let skylightLoads = await this.calcSkylightSensibleLoads()
    await ctx.briefWait()
    ctx.setProgress("Partitions")
    let partitionLoads = await this.calcPartitionSensibleLoads()
    await ctx.briefWait()
    ctx.setProgress("Floors")
    let floorLoads = await this.calcFloorSensibleLoads();
    await ctx.briefWait()

    ctx.setProgress("Appliances")
    let applianceLoads = await this.calcApplianceSensibleLoads()
    await ctx.briefWait()
    ctx.setProgress("Lighting")
    let lightingLoads = await this.calcLightingSensibleLoads()
    await ctx.briefWait()
    await ctx.briefWait()
    ctx.setProgress("People")
    let peopleLoads = await this.calcPeopleSensibleLoads()
    await ctx.briefWait()
    ctx.setProgress("Misc")
    let miscLoads = await this.calcMiscSensibleLoads()
    await ctx.briefWait()
    ctx.setProgress("Infiltration")
    let infiltrationLoads = await this.calcInfiltrationSensibleLoads()
    await ctx.briefWait()

    let totalLoads = MatrixUtils.sumOfMatrices([
      wallLoads.SensibleLoadsMatrix,
      roofLoads.SensibleLoadsMatrix,
      windowLoads.SensibleLoadsMatrix,
      doorLoads.SensibleLoadsMatrix,
      skylightLoads.SensibleLoadsMatrix,
      partitionLoads.SensibleLoadsMatrix,
      floorLoads.SensibleLoadsMatrix,
      applianceLoads.SensibleLoadsMatrix,
      lightingLoads.SensibleLoadsMatrix,
      peopleLoads.SensibleLoadsMatrix,
      miscLoads.SensibleLoadsMatrix,
      infiltrationLoads.SensibleLoadsMatrix,
    ])
    ctx.logLoadMatrix('Total sensible loads', totalLoads)

    // Note: the plenum loads are kept separate from the rest of
    // the sensible loads
    let totalPlenumLoads = MatrixUtils.sumOfMatrices([
      wallLoads.PlenumLoadsMatrix,
      roofLoads.PlenumLoadsMatrix,
      lightingLoads.PlenumLoadsMatrix,
    ])
    ctx.logLoadMatrix('Total plenum loads', totalPlenumLoads)

    // Combine all breakdowns
    let combinedBreakdown = {
      label: 'Sensible Loads',
      value: totalLoads,
      children: {
        "Walls": {
          label: "Walls",
          value: wallLoads.SensibleLoadsMatrix,
          children: wallLoads.Breakdown,
        },
        "Roofs": {
          label: "Roofs",
          value: roofLoads.SensibleLoadsMatrix,
          children: roofLoads.Breakdown,
        },
        "Windows": {
          label: "Windows",
          value: windowLoads.SensibleLoadsMatrix,
          children: windowLoads.Breakdown,
        },
        "Doors": {
          label: "Doors",
          value: doorLoads.SensibleLoadsMatrix,
          children: doorLoads.Breakdown,
        },
        "Skylights": {
          label: "Skylights",
          value: skylightLoads.SensibleLoadsMatrix,
          children: skylightLoads.Breakdown,
        },
        "Partitions": {
          label: "Partitions",
          value: partitionLoads.SensibleLoadsMatrix,
          children: partitionLoads.Breakdown,
        },
        "Floors": {
          label: "Floors",
          value: floorLoads.SensibleLoadsMatrix,
          children: floorLoads.Breakdown,
        },
        "Appliances": {
          label: "Appliances",
          value: applianceLoads.SensibleLoadsMatrix,
          children: applianceLoads.Breakdown,
        },
        "Lighting": {
          label: "Lighting",
          value: lightingLoads.SensibleLoadsMatrix,
          children: lightingLoads.Breakdown,
        },
        "People": {
          label: "People",
          value: peopleLoads.SensibleLoadsMatrix,
          children: peopleLoads.Breakdown,
        },
        "Misc": {
          label: "Miscellaneous",
          value: miscLoads.SensibleLoadsMatrix,
          children: miscLoads.Breakdown,
        },
        "Infiltration": {
          label: "Infiltration",
          value: infiltrationLoads.SensibleLoadsMatrix,
          children: infiltrationLoads.Breakdown,
        },
      }
    }

    let res = {
      totalLoads: totalLoads,
      totalPlenumLoads: totalPlenumLoads,
      Breakdown: combinedBreakdown,

      wallLoads: wallLoads,
      roofLoads: roofLoads,
      windowLoads: windowLoads,
      doorLoads: doorLoads,
      skylightLoads: skylightLoads,
      partitionLoads: partitionLoads,
      floorLoads: floorLoads,
      applianceLoads: applianceLoads,
      lightingLoads: lightingLoads,
      peopleLoads: peopleLoads,
      miscLoads: miscLoads,
      infiltrationLoads: infiltrationLoads,
    }

    ctx.endProgressSection()
    ctx.endSection()
    return res
  }

  async calcOutputs() {
    let ctx = this.ctx;
    ctx.startSection('Space Cooling')
    ctx.startProgressSection("Space cooling", {
      "Latent loads": {},
      "Sensible loads": {},
    })

    let solarValues = await this.calcSolarValues()

    let latentLoadsRes = null
    if (valOr(ctx.debugOptions.Calc_latent_loads, true)) {
      ctx.setProgress("Latent loads")
      latentLoadsRes = await this.calcLatentLoads()
    }
    let sensibleLoadsRes = null
    if (valOr(ctx.debugOptions.Calc_sensible_loads, true)) {
      ctx.setProgress("Sensible loads")
      sensibleLoadsRes = await this.calcSensibleLoads()
    }

    if (!latentLoadsRes || !sensibleLoadsRes) {
      throw new Error("Aborting early - latent or sensible loads not calculated (debug mode)")
    }

    let combinedBreakdown = {
      totalSensibleLoad: sensibleLoadsRes.Breakdown,
      totalLatentLoad: latentLoadsRes.Breakdown,
    }

    let inputs = {
      dayOfMonth: ctx.toplevelData.dayOfMonth,
      locationData: ctx.toplevelData.locationData,
      designTemps: ctx.designTemps,
      t_i_summer: ctx.t_i_summer,
      t_i_winter: ctx.t_i_winter,
      zone_humidity: ctx.zone_humidity,
    }
    let res = {
      q_sensible: sensibleLoadsRes.totalLoads,
      q_latent: latentLoadsRes.totalLoads,
      q_total: math.add(sensibleLoadsRes.totalLoads, latentLoadsRes.totalLoads),
      q_plenum: sensibleLoadsRes.totalPlenumLoads,

      SensibleLoads: sensibleLoadsRes,
      LatentLoads: latentLoadsRes,

      Inputs: inputs,
      Breakdown: combinedBreakdown,

      Debug: {
        SolarValues: solarValues,
      }
    }
    res.resultsNode = new SpaceCoolingResultsNode(this.space, res, this.opts);
    res.sunburstNode = res.resultsNode.sunburstNode;

    ctx.endProgressSection()
    ctx.endSection()
    return res
  }
};

export class SpaceCoolingResultsNode extends ResultsNode {
  constructor(space, loadResults, opts) {
    super(ResultsNodeType.VerticalSplit, 'Space Cooling');
    this.space = space;
    this.loadResults = loadResults;

    this.title = "Space Cooling"
    this.helpText = `This section shows the cooling loads for your space. Cooling loads are calculated ` +
      `for one day of each month of the year (typically the 21st). We provide extra detail for the month and hour that experience the highest load.`

    //let summaryNode = this._getSummaryNode(loadResults)
    //this.children.push(summaryNode)

    let monthGraphNode = this._getPeakLoadsByMonthNode(loadResults)
    this.children.push(monthGraphNode)

    //let detailedMonthGraphNode = this._getDetailedPeakLoadsByMonthNode(loadResults)
    //this.children.push(detailedMonthGraphNode)

    let peakValue = findMaxLoadInMatrix(loadResults.q_total)
    let peakTime = peakValue.time

    let sampleMonthNode = this._getLoadResultsNodeForMonth(loadResults, peakTime[0])
    this.children.push(sampleMonthNode)

    let sampleHourNode = this._getLoadResultsNodeForHour(loadResults, peakTime[0], peakTime[1])
    this.children.push(sampleHourNode)

    let isForSystemLoadCalcs = valOr(opts.isForSystemLoadCalcs, false);
    if (!isForSystemLoadCalcs) {
      // Only include these additional charts when we want detailed space results.
      // When calculating Space results for system calculations, we don't include these because
      // it's too much information.

      let coolingDesignTempsNode = this._getCoolingDesignTempsNode(loadResults, peakTime[0])
      this.children.push(coolingDesignTempsNode)

      if (this._includeSolarCharts()) {
        let solarNodes = SpaceCoolingResultsNode.makeSolarChartNodes(loadResults, peakTime[0]);
        this.children.push(...solarNodes);
      }
    }

    // Allow accessing the sunburst node directly, for PDF exports
    this.sunburstNode = sampleHourNode;
  }

  _includeSolarCharts() {
    // Only include solar charts if the space has walls or roofs
    return this.space.walls.length > 0 || this.space.roofs.length > 0;
  }

  _getSummaryNode(loadResults) {
    let peakValue = findMaxLoadInMatrix(loadResults.q_total)
    let peakTime = peakValue.time
    let sensibleLoadAtPeak = loadResults.q_sensible.get(peakTime)
    let latentLoadAtPeak = loadResults.q_latent.get(peakTime)

    //let inputs = loadResults.Inputs;
    let peakCoolingLoad = Quantity.create(peakValue.value, Units.Load);
    let sensibleLoadAtPeakQty = Quantity.create(sensibleLoadAtPeak, Units.Load);
    let latentLoadAtPeakQty = Quantity.create(latentLoadAtPeak, Units.Load);
    let data = {
      title: 'Cooling Results Summary',
      rows: [
        {
          label: 'Peak Cooling Load',
          value: peakCoolingLoad.value,
          units: peakCoolingLoad.units,
          precision: 0,
        },
        {
          label: 'Sensible Load at Peak',
          value: sensibleLoadAtPeakQty.value,
          units: sensibleLoadAtPeakQty.units,
          precision: 0,
        },
        {
          label: 'Latent Load at Peak',
          value: latentLoadAtPeakQty.value,
          units: latentLoadAtPeakQty.units,
          precision: 0,
        },
        {
          label: 'Time of Peak Load',
          value: `${getMonthHourString(peakTime[0], peakTime[1])}`,
          units: Units.None,
        }
      ]
    }
    return new SummaryTableNode(data)
  }

  _getPeakLoadsByMonthNode(loadResults) {
    let monthLabels = Array.from({length: 12}, (_, i) => getShortMonthName(i));
    let peakValues = []
    for (let monthIndex = 0; monthIndex < 12; ++monthIndex) {
      let peakValue = findMaxLoadForMonth(loadResults.q_total, monthIndex)
      peakValues.push(peakValue.value)
    }
    //console.log("PEAK VALUES: ", peakValues)
    let monthChartData = {
      title: 'Peak Cooling Loads',
      units: Units.Load,
      precision: 0,
      type: 'Bar',
      labelX: 'Month',
      labelY: `Peak Load`,
      labels: monthLabels,
      values: peakValues,
      maxWidth: 800,
      height: 500,
      highlightPeak: true,
    }
    let monthGraphNode = new LineChartNode(monthChartData)
    monthGraphNode.data.helpText = `This chart shows the peak cooling load for each month. The peak load is the ` +
      `maximum cooling load of any hour on the test day for that month (typically the 21st).`
    return monthGraphNode
  }

  _getDetailedPeakLoadsByMonthNode(loadResults) {
    let monthLabels = Array.from({length: 12}, (_, i) => getShortMonthName(i));
    
    // Find peak hour for each month
    let peakHours = [];
    for (let monthIndex = 0; monthIndex < 12; ++monthIndex) {
      let peakValue = findMaxLoadForMonth(loadResults.q_total, monthIndex);
      peakHours.push(peakValue.hour); // Store the hour of peak load
    }
    
    // Define the component categories we want to show
    const componentCategories = [
      { key: 'Walls', label: 'Walls', source: loadResults.SensibleLoads.wallLoads.SensibleLoadsMatrix },
      { key: 'Roofs', label: 'Roofs', source: loadResults.SensibleLoads.roofLoads.SensibleLoadsMatrix },
      { key: 'Windows', label: 'Windows', source: loadResults.SensibleLoads.windowLoads.SensibleLoadsMatrix },
      { key: 'Doors', label: 'Doors', source: loadResults.SensibleLoads.doorLoads.SensibleLoadsMatrix },
      { key: 'Skylights', label: 'Skylights', source: loadResults.SensibleLoads.skylightLoads.SensibleLoadsMatrix },
      { key: 'People', label: 'People', source: loadResults.SensibleLoads.peopleLoads.SensibleLoadsMatrix },
      { key: 'Lighting', label: 'Lighting', source: loadResults.SensibleLoads.lightingLoads.SensibleLoadsMatrix },
      { key: 'Appliances', label: 'Appliances', source: loadResults.SensibleLoads.applianceLoads.SensibleLoadsMatrix },
      { key: 'Infiltration', label: 'Infiltration', source: loadResults.SensibleLoads.infiltrationLoads.SensibleLoadsMatrix },
      { key: 'Latent', label: 'Latent', source: loadResults.q_latent },
      { key: 'Other', label: 'Other', source: null } // Will calculate this as the remainder
    ];
    
    // Create datasets for each component
    let datasets = [];
    let componentData = [];
    
    // First pass: extract values for defined components
    for (const category of componentCategories) {
      if (category.key === 'Other') continue; // Skip 'Other' for now
      
      let data = new Array(12).fill(0);
      if (category.source) {
        for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
          // Get the value at the peak hour for this month
          data[monthIndex] = category.source.get([monthIndex, peakHours[monthIndex]]);
        }
      }
      
      // Calculate total value for this component
      const totalValue = data.reduce((sum, val) => sum + val, 0);
      
      // Only include components that have non-zero values
      if (totalValue !== 0) {
        componentData.push({
          label: category.label,
          data: data,
          totalValue: totalValue // Calculate total for sorting
        });
      }
    }
    
    // Second pass: calculate 'Other' as the remainder
    let otherValues = new Array(12).fill(0);
    for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
      let total = loadResults.q_total.get([monthIndex, peakHours[monthIndex]]);
      let sum = 0;
      
      for (const component of componentData) {
        sum += component.data[monthIndex];
      }
      
      otherValues[monthIndex] = total - sum;
    }
    
    // Add 'Other' to component data if it has non-zero values
    const otherTotal = otherValues.reduce((sum, val) => sum + val, 0);
    if (otherTotal !== 0) {
      componentData.push({
        label: 'Other',
        data: otherValues,
        totalValue: otherTotal
      });
    }
    
    // Sort components by total contribution (largest first)
    componentData.sort((a, b) => b.totalValue - a.totalValue);
    
    // Create datasets in sorted order (largest at bottom)
    for (let i = 0; i < componentData.length; i++) {
      datasets.push({
        label: componentData[i].label,
        data: componentData[i].data
      });
    }
    
    // Create the chart data object
    let stackedBarData = {
      title: 'Peak Cooling Loads - Breakdown at Peak Hour',
      units: Units.Load,
      labelX: 'Month',
      labelY: `Load`,
      labels: monthLabels,
      datasets: datasets,
      maxWidth: 800,
      height: 550,
      helpText: `This chart is a more detailed view of the chart above. It shows the cooling load breakdown ` +
        ` during the peak hour of the test day (typically the 21st) for each month.`
    };
    
    // Add day and peak hour information to the title
    for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
      monthLabels[monthIndex] += ` ${loadResults.Inputs.dayOfMonth} (${getHourString(peakHours[monthIndex])})`;
    }
    
    return new StackedBarChartNode(stackedBarData);
  }

  _getLoadResultsNodeForMonth(loadResults, monthIndex) {
    /*
    let hourLabels = Array.from({length: 24}, (_, i) => getHourString(i));
    let values = []
    for (let hr = 0; hr < 24; ++hr) {
      let value = loadResults.q_total.get([monthIndex, hr])
      values.push(value)
    }
    let byHourChartData = {
      title: `Hourly Cooling Loads - ${getShortMonthName(monthIndex)} ${loadResults.Inputs.dayOfMonth}`,
      units: Units.Load,
      type: 'Line',
      labelX: 'Hour',
      labelY: `Load`,
      labels: hourLabels,
      values: values,
      maxWidth: 800,
      height: 500,
      highlightPeak: true,
    }
    let hourGraphNode = new LineChartNode(byHourChartData)
    hourGraphNode.data.helpText = `This chart shows the cooling loads for each hour of the test day for the given month.`
    */
    
    // Create stacked bar chart showing breakdown by component
    let stackedBarData = this._createComponentBreakdownStackedBarData(loadResults, monthIndex);
    let stackedBarNode = new StackedBarChartNode(stackedBarData);
    
    // Create a container node to hold both charts
    let containerNode = new ResultsNode(ResultsNodeType.VerticalSplit, 'Monthly Loads');
    //containerNode.children.push(hourGraphNode);
    containerNode.children.push(stackedBarNode);
    
    return containerNode;
  }
  
  _createComponentBreakdownStackedBarData(loadResults, monthIndex) {
    let hourLabels = Array.from({length: 24}, (_, i) => getHourString(i));
    
    // Define the component categories we want to show
    const componentCategories = [
      { key: 'Walls', label: 'Walls', source: loadResults.SensibleLoads.wallLoads.SensibleLoadsMatrix },
      { key: 'Roofs', label: 'Roofs', source: loadResults.SensibleLoads.roofLoads.SensibleLoadsMatrix },
      { key: 'Windows', label: 'Windows', source: loadResults.SensibleLoads.windowLoads.SensibleLoadsMatrix },
      { key: 'Doors', label: 'Doors', source: loadResults.SensibleLoads.doorLoads.SensibleLoadsMatrix },
      { key: 'Skylights', label: 'Skylights', source: loadResults.SensibleLoads.skylightLoads.SensibleLoadsMatrix },
      { key: 'People', label: 'People', source: loadResults.SensibleLoads.peopleLoads.SensibleLoadsMatrix },
      { key: 'Lighting', label: 'Lighting', source: loadResults.SensibleLoads.lightingLoads.SensibleLoadsMatrix },
      { key: 'Appliances', label: 'Appliances', source: loadResults.SensibleLoads.applianceLoads.SensibleLoadsMatrix },
      { key: 'Infiltration', label: 'Infiltration', source: loadResults.SensibleLoads.infiltrationLoads.SensibleLoadsMatrix },
      { key: 'Latent', label: 'Latent', source: loadResults.q_latent },
      { key: 'Other', label: 'Other', source: null } // Will calculate this as the remainder
    ];
    
    // Create datasets for each component
    let datasets = [];
    let otherValues = new Array(24).fill(0);
    let componentData = [];
    
    // First pass: extract values for defined components
    for (const category of componentCategories) {
      if (category.key === 'Other') continue; // Skip 'Other' for now
      
      let data = new Array(24).fill(0);
      if (category.source) {
        for (let hr = 0; hr < 24; hr++) {
          data[hr] = category.source.get([monthIndex, hr]);
        }
      }
      
      // Calculate total value for this component
      const totalValue = data.reduce((sum, val) => sum + val, 0);
      
      // Only include components that have non-zero values
      if (totalValue !== 0) {
        componentData.push({
          label: category.label,
          data: data,
          totalValue: totalValue // Calculate total for sorting
        });
      }
    }
    
    // Second pass: calculate 'Other' as the remainder
    for (let hr = 0; hr < 24; hr++) {
      let total = loadResults.q_total.get([monthIndex, hr]);
      let sum = 0;
      
      for (const component of componentData) {
        sum += component.data[hr];
      }
      
      otherValues[hr] = total - sum;
    }
    
    // Add 'Other' to component data
    componentData.push({
      label: 'Other',
      data: otherValues,
      totalValue: otherValues.reduce((sum, val) => sum + val, 0)
    });
    
    // Sort components by total contribution (largest first)
    componentData.sort((a, b) => b.totalValue - a.totalValue);
    
    // Create datasets in sorted order (largest at bottom)
    for (let i = 0; i < componentData.length; i++) {
      datasets.push({
        label: componentData[i].label,
        data: componentData[i].data
      });
    }
    
    // Create the chart data object
    return {
      title: `Cooling Loads on ${getShortMonthName(monthIndex)} ${loadResults.Inputs.dayOfMonth}`,
      units: Units.Load,
      precision: 0,
      labelX: 'Hour',
      labelY: `Load`,
      labels: hourLabels,
      datasets: datasets,
      maxWidth: 800,
      height: 550,
      helpText: 'This chart shows the breakdown of cooling loads by component for each hour of the day.'
    };
  }

  _getLoadResultsNodeForHour(loadResults, monthIndex, hourIndex) {
    let title = `Cooling Loads at ${getShortMonthName(monthIndex)} ${loadResults.Inputs.dayOfMonth}, ${getHourString(hourIndex)}`
    let sunburstData = {
      title: title,
      units: Units.Load,
      precision: 0,
      includeTable: true,
      data: SunburstNode.getBreakdownForMonthHour(loadResults.Breakdown, monthIndex, hourIndex),
    }
    let sunburstNode = new SunburstNode(sunburstData)
    sunburstNode.data.helpText = `This chart shows the breakdown of the cooling loads during the peak hour.`;
    return sunburstNode
  }

  _getCoolingDesignTempsNode(loadResults, monthIndex) {
    let designTemps = loadResults.Inputs.designTemps;
    let hourLabels = Array.from({length: 24}, (_, i) => getHourString(i));
    let dryBulbValues = [];
    let wetBulbValues = [];
    
    for (let hr = 0; hr < 24; ++hr) {
      let ctx = CalcContext.create();
      let dryBulbValue = designTemps.getCoolingDryBulbOut(ctx, monthIndex, hr);
      let wetBulbValue = designTemps.getCoolingWetBulbOut(ctx, monthIndex, hr);
      dryBulbValues.push(dryBulbValue);
      wetBulbValues.push(wetBulbValue);
    }
    
    let chartData = {
      title: `Outdoor Temperatures (${designTemps.getCoolingDesignTempStr()}) - ${getShortMonthName(monthIndex)} ${loadResults.Inputs.dayOfMonth}`,
      labelX: 'Hour',
      labelY: `Temperature`,
      labels: hourLabels,
      units: Units.F,
      series: [
        {
          name: "Dry Bulb",
          values: dryBulbValues,
          color: "#ff7043" // orange-red for dry bulb
        },
        {
          name: "Wet Bulb",
          values: wetBulbValues,
          color: "#42a5f5" // blue for wet bulb
        }
      ],
      maxWidth: 800,
      height: 500,
      showMinMaxLabels: true
    };
    
    let graphNode = new MultiLineChartNode(chartData);
    graphNode.data.helpText = `This chart shows the outdoor dry-bulb and wet-bulb temperatures used for cooling load calculations during the peak load day. ` +
      `These values are taken from the weather data for your location.`;
    return graphNode;
  }

  static makeSolarChartNodes(loadResults, monthIndex) {
    let nodes = [];
    let solarValuesNode = SpaceCoolingResultsNode.getSolarValuesNode(loadResults, monthIndex)
    nodes.push(solarValuesNode)

    let solarValuesByMonthNode = SpaceCoolingResultsNode.getSolarValuesByMonthNode(loadResults)
    nodes.push(solarValuesByMonthNode)
    
    //let avgSolarValuesByMonthNode = SpaceCoolingResultsNode.getAvgSolarValuesByMonthNode(loadResults)
    //nodes.push(avgSolarValuesByMonthNode)

    let sunPlotNode = SpaceCoolingResultsNode.getSunPolarPlotNode(loadResults, monthIndex)
    nodes.push(sunPlotNode)

    return nodes;
  }

  static getSolarValuesNode(loadResults, monthIndex) {
    let solarValues = loadResults.Debug.SolarValues;
    let primarySurfaces = solar.getPrimarySurfacesMap();
    
    // Create arrays to hold data for each hour
    let hourLabels = [];
    let primarySurfacesSeries = [];
    
    // Initialize series for each primary surface
    let index = 0;
    for (const key in primarySurfaces) {
      const surfaceName = primarySurfaces[key].label;
      
      primarySurfacesSeries.push({
        name: surfaceName,
        values: [],
        fill: false,
      });
      index++;
    }
    
    // Find first and last hour with non-zero irradiance
    let firstHourWithSun = 24;
    let lastHourWithSun = 0;
    
    for (let hr = 0; hr < 24; ++hr) {
      let solarData = solarValues.get(monthIndex, hr);
      let hasIrradiance = false;
      
      // Check if any surface has non-zero irradiance
      for (const key in primarySurfaces) {
        if (solarData.primarySurfaceValues[key].E_t > 0) {
          hasIrradiance = true;
          break;
        }
      }
      
      if (hasIrradiance) {
        firstHourWithSun = Math.min(firstHourWithSun, hr);
        lastHourWithSun = Math.max(lastHourWithSun, hr);
      }
    }
    
    // If no sun found, default to showing full day
    if (firstHourWithSun > lastHourWithSun) {
      firstHourWithSun = 0;
      lastHourWithSun = 23;
    }
    
    // Include one hour before and after the sun period to show zero values
    // (but don't go outside the 0-23 range)
    let displayFirstHour = Math.max(0, firstHourWithSun - 1);
    let displayLastHour = Math.min(23, lastHourWithSun + 1);
    
    // Populate the series with data for the display range
    for (let hr = displayFirstHour; hr <= displayLastHour; ++hr) {
      let solarData = solarValues.get(monthIndex, hr);
      
      // Add hour label
      hourLabels.push(getHourString(hr));
      
      // Add values for each primary surface
      let surfaceIndex = 0;
      for (const key in primarySurfaces) {
        let surfaceValue = solarData.primarySurfaceValues[key].E_t;
        primarySurfacesSeries[surfaceIndex].values.push(surfaceValue);
        surfaceIndex++;
      }
    }
    
    // Combine all series
    let allSeries = [...primarySurfacesSeries];
    
    let chartData = {
      title: `Solar Irradiance - ${getShortMonthName(monthIndex)} ${loadResults.Inputs.dayOfMonth}`,
      units: Units.LoadPerArea,
      precision: 0,
      labelX: 'Hour',
      labelY: `Solar Irradiance`,
      labels: hourLabels,
      series: allSeries,
      maxWidth: 800,
      height: 500,
      showMinMaxLabels: false,
    };
    
    let graphNode = new MultiLineChartNode(chartData);
    graphNode.data.helpText = `This chart shows the total solar irradiance for common surfaces during the peak load day. The total irradiance on a ` +
      `surface is the sum of direct-beam, diffuse, and reflected components. ` +
      `This data is specific to your location.`;

    return graphNode;
  }

  static getSolarValuesByMonthNode(loadResults) {
    let solarValues = loadResults.Debug.SolarValues;
    let primarySurfaces = solar.getPrimarySurfacesMap();
    let monthLabels = Array.from({length: 12}, (_, i) => getShortMonthName(i));
    
    // Create arrays to hold peak data for each month
    let primarySurfacesSeries = [];
    
    // Initialize series for each primary surface
    for (const key in primarySurfaces) {
      const surfaceName = primarySurfaces[key].label;
      
      primarySurfacesSeries.push({
        name: surfaceName,
        values: new Array(12).fill(0), // One value per month
      });
    }
    
    // Find peak irradiance for each surface for each month
    for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
      // For each surface, find the peak value during this month
      let surfaceIndex = 0;
      for (const key in primarySurfaces) {
        let peakValue = 0;
        
        // Check all hours to find peak
        for (let hr = 0; hr < 24; hr++) {
          let solarData = solarValues.get(monthIndex, hr);
          let surfaceValue = solarData.primarySurfaceValues[key].E_t;
          peakValue = Math.max(peakValue, surfaceValue);
        }
        
        // Store the peak value for this surface and month
        primarySurfacesSeries[surfaceIndex].values[monthIndex] = peakValue;
        surfaceIndex++;
      }
    }
    
    let chartData = {
      title: `Peak Solar Irradiance by Month`,
      units: Units.LoadPerArea,
      precision: 0,
      labelX: 'Month',
      labelY: `Peak Solar Irradiance`,
      labels: monthLabels,
      series: primarySurfacesSeries,
      maxWidth: 800,
      height: 500,
      showMinMaxLabels: false,
    };
    
    let graphNode = new MultiLineChartNode(chartData);
    graphNode.data.helpText = `This chart shows the peak solar irradiance for common surfaces during each month of the year. ` +
      `The peak value is the maximum irradiance that occurs at any hour during the test day for that month (typically the 21st). ` +
      `This data is specific to your location and helps visualize seasonal solar patterns.`;

    return graphNode;
  }
  
  static getAvgSolarValuesByMonthNode(loadResults) {
    let solarValues = loadResults.Debug.SolarValues;
    let primarySurfaces = solar.getPrimarySurfacesMap();
    let monthLabels = Array.from({length: 12}, (_, i) => getShortMonthName(i));
    
    // Create arrays to hold average data for each month
    let primarySurfacesSeries = [];
    
    // Initialize series for each primary surface
    for (const key in primarySurfaces) {
      const surfaceName = primarySurfaces[key].label;
      
      primarySurfacesSeries.push({
        name: surfaceName,
        values: new Array(12).fill(0), // One value per month
      });
    }
    
    // Calculate average irradiance for each surface for each month
    for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
      // For each surface, calculate the average value during daylight hours
      let surfaceIndex = 0;
      for (const key in primarySurfaces) {
        let totalValue = 0;
        let daylightHours = 0;
        
        // Sum values for all hours with sunlight
        for (let hr = 0; hr < 24; hr++) {
          let solarData = solarValues.get(monthIndex, hr);
          let surfaceValue = solarData.primarySurfaceValues[key].E_t;
          
          // Only include hours with positive irradiance
          if (surfaceValue > 0) {
            totalValue += surfaceValue;
            daylightHours++;
          }
        }
        
        // Calculate and store the average value for this surface and month
        let avgValue = daylightHours > 0 ? totalValue / daylightHours : 0;
        primarySurfacesSeries[surfaceIndex].values[monthIndex] = avgValue;
        surfaceIndex++;
      }
    }
    
    let chartData = {
      title: `Average Daytime Solar Irradiance by Month`,
      units: Units.LoadPerArea,
      precision: 0,
      labelX: 'Month',
      labelY: `Average Solar Irradiance`,
      labels: monthLabels,
      series: primarySurfacesSeries,
      maxWidth: 800,
      height: 500,
      showMinMaxLabels: false,
    };
    
    let graphNode = new MultiLineChartNode(chartData);
    graphNode.data.helpText = `This chart shows the average solar irradiance during daylight hours for common surfaces during each month of the year. ` +
      `The average is calculated using only hours when the sun is above the horizon on the test day for that month (typically the 21st). ` +
      `This data is specific to your location and helps visualize seasonal solar patterns.`;

    return graphNode;
  }

  static getSunPolarPlotNode(loadResults, monthIndex) {
    let solarValues = loadResults.Debug.SolarValues;
    let points = []
    for (let hr = 0; hr < 24; ++hr) {
      let values = solarValues.get(monthIndex, hr).clearSkyValues;
      // Only plot points where the sun is visible
      if (values.solar_altitude_rads < 0) {
        continue;
      }
      let point = {
        angle: toDegs(values.solar_azimuth_rads),
        radius: toDegs(values.solar_altitude_rads),
        label: `${getHourString(hr)}`,
      }
      points.push(point)
    }
    let chartData = {
      title: `Solar Position - ${getShortMonthName(monthIndex)} ${loadResults.Inputs.dayOfMonth}`,
      points: points,
      angleStep: 30,
      showLabels: true,
      maxWidth: 600,
      height: 600,
      startValue: 90,
      endValue: 0,
      radiusStep: 10,
      valueUnit: Units.getLabel(Units.Degrees),
    }
    let node = new PolarPlotNode(chartData)
    node.data.helpText = `This plot shows the sun position relative to your site during the given day. The angle is the solar azimuth angle and the radius is the solar altitude angle (angle above the horizon). ` +
      ` The altitude is 90 degrees when the sun is directly overhead and 0 degrees when on the horizon. By convention, the azimuth angle is 0 when the sun is due south,  90 degrees when due west, and -90 degrees when due east.`;

    return node
  }
}
